// configurator-quantify.js
// Quantify rebuild configurator (build order step 6, v1 test round).
// Posts the Section 2.2 payload to the whl-create-bundle Supabase edge function
// and consumes its response contract (bundleVariantId, variantId, total in cents,
// isDuplicate, bundleHandle). Quote cart integration is carried over from the
// legacy configurator unchanged: localStorage sms_quote_cart fallback, quote
// threshold, SMS_QuoteModal, QuoteSuccessDialog, quote-cart-updated event.
// Warranty markups come ONLY from the settings action (single source rule).
// Memory: one radio card per module x count pair; qty carried in data-qty and
// pricing handled by the generic group loop; the capacity dropdown filters cards
// by data-total-gb. The RAM Clock Speed filter has a Clear Selection link
// (.q-clear-speed-filter) relocated by JS into the speed button row itself; it
// deactivates the speed filter so all speeds render for comparison (2026-08-10;
// capacity filter and selection untouched).
// Card prices render absolute on the first line with the bracketed delta below
// (owner preference). When a group has NO selection (scratch state), every card
// shows its plain absolute price, since the delta would equal it (2026-08-20).
// Drives: Recommended group always visible; per-family inline expanders with
// Show/Hide label swap. Drive quantity IS the selection: steppers start at 0,
// qty > 0 auto-adds, back to 0 auto-removes, no Add button; cross-family bay
// clamp enforced globally (plus buttons freeze, typed values clamp).
// TDP (reworked 2026-08-12): computeTdpBreakdown() builds a per-section watts
// breakdown (chassis base plus steps 1-8; OS draws nothing and is omitted).
// Drive watts DEDUPE BY QSKU: twin cards (Recommended plus home group, the
// duplicate-not-remove ruling) share one qsku and must count ONCE; the old loop
// counted both DOM inputs and inflated TDP. A tooltip trigger injected next to
// .product-summary-watts shows every row INCLUDING 0W rows (owner ruling
// 2026-08-12: zeros stay visible so buyers never wonder whether a category was
// included, and data gaps surface as suspicious zeros).
// Instructions boxes (owner request 2026-08-12): configurator-info-box renders
// as a .q-info-expander details, collapsed by default, plus glyph flipping to
// minus when open; the section summary click handler excludes it (same pattern
// as .q-drive-expander) so it toggles independently. Its styles live in the
// shared injected style block below.
// Scratch / Popular Configuration (owner request 2026-08-20): the snippet
// renders a control bar under Configure Your System with a live
// "Currently Configured" subtotal (class product-summary-price, so recalc's
// single total write updates it), a Start from Scratch button that clears every
// selection down to the chassis kit (heatsink to Standard, NIC to None,
// warranty to base tier, drives to 0, all other groups unchecked), and a
// Popular Configuration button that re-applies the companion defaults captured
// at init (captureDefaultSnapshot). The "From $" static price is gone from the
// snippet. Buttons sit ABOVE the subtotal line, left aligned; the button
// matching the current baseline carries q-config-btn--active (Popular on load
// since Liquid renders the companion defaults), and any user change event
// bubbling to the component clears the highlight because the build no longer
// matches either baseline (programmatic writes fire no change events, so the
// buttons' own state sets survive). Add To Cart / Add To Quote alerts now
// itemize which required sections are missing (missingSelections) instead of
// a generic message. Card tightening (2026-08-20): the injected style block
// zeroes .configurator__card-title margins entirely (base CSS only zeroes the
// .6em bottom margin when an EMPTY description element follows; cards with no
// description element kept the gap) and trims card-content vertical padding
// to .5em, so titles center against the radio and cards sit tighter. Drive
// card category meta (.configurator__card-meta, e.g. "Refurbished SAS SSD")
// is hidden via the same block: redundant with the drive title and the group
// heading (owner request 2026-08-20); markup stays in the snippet for easy
// revert.
// Tray pricing (owner-approved 2026-08-26): the settings call now sends the
// chassis kit qsku and whl-create-bundle v29 returns a tray map (per drive
// form factor: tray qsku, qty per drive, dime-rounded unit cents from the
// same resolve_tray path the backend uses). baseTotalCents() adds tray cents
// for every selected drive, so the pre-quote bottom price matches the quoted
// total; warranty markup applies on top of tray cost on both sides. If the
// map is absent (older function version or resolution failure) behavior
// falls back to the previous display and the backend total remains
// authoritative.
// Summary pane renderer (wizard redesign, mock v11 port, 2026-08-26): the
// section file on the redesign theme adds a desktop Build Summary pane
// (#q-led-lines / #q-pane) and a mobile bar expander (#q-bar-expander).
// renderSummaryPane() runs at the END of recalc() and builds numbered ledger
// lines from the SAME selection readers recalc uses (never parallel state),
// including auto-flagged tray lines from trayMap and a grouped drives box.
// Validation keeps the red-after-attempt ruling: on a failed Add To Cart /
// Add To Quote the pane shows clickable error notices (scrollIntoView to the
// offending section) instead of the legacy alert; the alert remains as the
// fallback when the pane shell is absent, so this same asset stays safe on
// themes without the redesign markup (everything pane-related no-ops there).
// setupSummaryPane() also mounts pane CTAs (shared addToCart/requestQuote
// flows with per-button spinners), fills the mobile bar expander with a
// mirrored ledger and wires its toggle, and tags .product-page with
// q-pane-live so the redesign CSS hides the fixed bar at desktop widths only
// when the pane is actually live (JS failure leaves the bar in place).
// Wizard redesign 4A (2026-08-26): section guides render as q-tip overlays
// toggled by q-tip-btn in each slot head (setupTips); the old q-info-expander
// path stays in code as an inert fallback for legacy markup. The summary
// collapse toggle excludes the tip button.
// Wizard redesign 4B MEMORY UX (2026-08-26): setupMemoryUx() synthesizes the
// mock v11 memory pattern (Speed segs, per-DIMM Module Size segs, ONE total
// capacity select with difference-priced tails, and a Populates line) from
// the Liquid-rendered memory cards; the hidden radios REMAIN the state
// carriers (seg/select actions check a radio and dispatch change, flowing
// through the exact same handlers as a card click; readers, payloads, and
// pane untouched). renderMemoryUx() runs inside recalc() so tails and seg
// state re-render on every pass. Speed labels harvest from the existing
// filter buttons; module size derives from data-total-gb / data-qty (same
// attributes, no label parsing). The legacy capacity dropdown, speed filter
// row, and card grid are hidden (q-mem-hidden) only AFTER a successful
// mount, and the whole feature gates on the redesign shell (#q-pane), so
// legacy markup keeps the old memory UI. Switching Speed or Module Size
// migrates the selection to the same DIMM count in the new list when that
// count exists there (mock behavior, honest price update through the normal
// change flow); when it does not, the existing selection is kept unchanged,
// never silently cleared.
// Wizard redesign 4C DRIVES UX (2026-08-26): setupDrivesUx() adds the mock
// v11 drives pattern on the redesign shell: a pinned "Your Drives" box
// (q-seldrives) above the list with per-row steppers, line totals, and a
// remove button; a Type seg (Recommended first, then one button per drive
// group) replacing the Show/Hide expanders with one group visible at a
// time (expander summaries hide, the active expander is forced open); and
// a bayline under the list ("N of X bays used", amber chassis-limit chip
// at max). selectedDrives and applyDriveQty's clamp remain the single
// source: the pinned steppers route through applyDriveQty, and
// renderDrivesUx() mirrors qty and selected state to EVERY input sharing a
// qsku each recalc pass, so Recommended/home-group twin cards can never
// disagree on screen. Gates on #q-pane; legacy markup keeps expanders.
// Wizard redesign 4D REMAINING (2026-08-26): setupWarrantyUx() replaces the
// warranty radio cards with mock tier buttons (180-Day/1/2/3-Year, live
// dollar amounts from the settings markups on the current base, honest
// .../unavailable placeholders) plus a per-tier description notice fed by
// the #q-warr-data div the section renders from the configurator_warranty
// metaobjects (the included 180-day copy is section-local). Buttons drive
// the EXISTING hidden radios and #q-warranty-years select, so payloads and
// pane readers are untouched. setupSectionNotices() adds mock .notice rows
// above the heatsink and NIC grids (heatsink carries an AUTO-SELECTED flag
// while the standard option is the selection). renderSummaryPane() now
// also drives the mobile bar chevron pill ("N lines" / "N items needed"
// err state) and swaps the bar label to "Partial total" on validation
// failures. TDP tooltip rows drop their "Step N:" prefixes and the total
// row reads "Estimated TDP Draw". All of it gates on the redesign shell.
// 4D punch (2026-08-26): _bundle_components now lists resolved tray lines
// (same trayMap aggregation as the pane; the backend already composed and
// priced them, only the display property omitted them). The pane renders
// every numbered slot on every pass: empty sections show a muted
// "None selected" placeholder line so the numbering never skips.
// 4D punch 2 (2026-08-26): summary lines carry data-q-sec and a delegated
// click opens and scrolls to that section (qGotoSlot / qGotoTarget); the
// Your Drives box goes faint yellow with the blue border kept (CSS); the
// mock v11 PSU wattline bar ports in: Estimated TDP Draw vs a single PSU
// rating parsed from the selected PSU title, orange fill past 80%, an
// amber nudge when hot and a red note when draw exceeds the rating.
// Display only, never blocking; hidden when no wattage parses.
// 4D punch 3 (2026-08-26): the PSU wattline gains a View TDP calculation
// trigger that opens the same per-section breakdown window the summary
// watts tooltip shows (its tip carries q-tdp-tip, so updateTdpTooltip
// fills it on every recalc; the wrap avoids the q-tdp-wrap class so the
// setupTdpTooltip already-mounted guard cannot false-trip). The summary
// pane renders a red over-rating notice under the Power Supply line when
// estimated draw exceeds the parsed single PSU rating (psuRatingWatts is
// the shared parse); clicking it navigates to the PSU section. Display
// only, never blocking.
// Tray display amendment (owner ruling Option A, 2026-08-27): drive card
// prices and the pinned Your Drives row amounts show DRIVE + TRAY per
// unit via qTrayAdderCents(ff), the single adder source (null trayMap,
// an ff with no entries, or an unpriced tray contributes nothing, so
// display reverts to drive-only and the backend stays fail-loud). The
// summary pane keeps the split (drive line at drive-only price plus the
// auto tray line) and baseTotalCents() is untouched: it already adds
// tray cents separately, so folding the adder in there would double
// count. Display only; backend, quote, bundle lines, and totals are
// unchanged. Supersedes mock v11's drive-only card rendering.
// ADD-ONS (owner-approved 2026-10-20): the snippet's section 10 renders one
// single-pick radio group per companion add_on_lists entry (ul.q-addon-grid
// carries data-addon-group = the radio name, None card checked by default).
// addonGroupNames() discovers the groups from the DOM; selectedAddons()
// reads each through readRadio (the None card has an empty qsku and qty 0,
// so it reads as no pick). Add-on groups join the hybrid absolute/delta
// price loop, baseTotalCents, buildLines (role 'addon', qty 1, after os),
// the _bundle_components property list (Add-On: title), the Popular
// snapshot, Scratch (back to None), and the summary pane as slot 10 (one
// pick renders as a line, several as a grouped Add-Ons box like drives,
// none as the muted placeholder). TDP breakdown unchanged (add-ons 0W,
// omitted like OS). Copy config reads buildLines, so the config string
// gains the add-on segments with no change there.
// Known v1 notes: Add To Cart fails on DRAFT test bundles by design (resolves at
// cutover when bundles publish); page prices may drift from engine prices until
// the whl-push cutover, the backend total is authoritative.

// Drive group cap (wizard fix item 13, owner go 2026-10-21): the companion
// field drive_group_limits (rendered by whl-render v16 from
// platform_option_group.max_qty) arrives as data-drive-group-limits, a JSON
// array of "{drivers_lists GID}|{max}". Each drive resolves to its group via
// the expander's data-group-gid (Recommended twins inherit from the home
// group). applyDriveQty clamps by group remaining as well as bay remaining;
// plus buttons freeze at the group max; the bayline gains one line per capped
// group. Page-only enforcement, same posture as the bay limit.
//
// Empty drive trays (owner go 2026-10-22, spec claude/empty-drive-trays-spec-v1.md,
// R-ET-1..5): the snippet renders one pinned card in the Drives section from
// the companion empty_tray field (the chassis's native tray product) with a
// stepper whose input carries class q-empty-tray-input. setupEmptyTrays binds
// it; applyEmptyTrayQty clamps 0..99 and stores this.emptyTrayQty, the single
// source. Empties are a LOOSE quantity: never in selectedDrives, never
// counted toward bays or group caps, never in the TDP dedupe, never a
// buildLines() line (R-ET-3). They price at the tray unit price in
// baseTotalCents (separate from the per-drive qTrayAdderCents adder, which is
// untouched), render in the summary pane Drives group and in
// _bundle_components, reset with Scratch and Popular (clearDrives), and go to
// the backend as empty_trays: { qty } on create and in the quote cart item
// configuration; whl-create-bundle v34 folds them into the native tray line.
// The drives bayline mounts before the card so the order reads groups,
// bayline, empties, notice.
class ServerConfiguratorQuantify extends HTMLElement {
  constructor() {
    super();
    this.selectedDrives = new Map(); // qsku -> {qty, unitCents, ff, title}
    this.maxDriveSlots = 0;
    this.qGroupLimits = new Map(); // drivers_lists GID -> max drives from that group
    this.qDriveGroupOf = new Map(); // drive qsku -> drivers_lists GID (home group)
    this.emptyTrayQty = 0; // Empty Drive Trays stepper (loose qty, no bay accounting, 2026-10-22)
    this.warrantyTiers = null; // [{label, markup_percent}]
    this.trayMap = null; // drive_ff -> [{tray_qsku, qty_per_drive, unit_cents}] (v29 settings)
    this.settingsError = null;
    this.defaultSnapshot = null; // companion defaults captured at init (Popular Configuration)
    this.validationAttempted = false; // redesign pane: red-after-attempt error notices
    this.qPaneReady = false;
    this.qNoticeDelegated = false;
    this.qBaseThumbHtml = '';
    this.qMemUxReady = false; // 4B memory UX (segs + capacity select)
    this.qMemModel = [];
    this.qMemSpeeds = [];
    this.qMemSpeedNames = {};
    this.qMemSpeed = null;
    this.qMemSize = 0;
    this.qMemLastSel = null;
    this.qDrvUxReady = false; // 4C drives UX (pinned selection + type seg)
    this.qDrvGroups = [];
    this.qDrvGroup = null;
    this.qDrvSelBox = null;
    this.qDrvSelHead = null;
    this.qDrvSelRows = null;
    this.qDrvBayline = null;
    this.qWarrUxReady = false; // 4D warranty tier buttons + description
    this.qWarrData = [];
    this.qWarrRow = null;
    this.qWarrDesc = null;

    window.addEventListener('pageshow', (event) => {
      if (event.persisted || (performance.getEntriesByType('navigation')[0]?.type === 'back_forward')) {
        window.location.reload();
      }
    });

    this.init();
  }

  init() {
    this.maxDriveSlots = parseInt(this.dataset.maxDrives) || 0;
    this.setupDriveGroupLimits();
    this.setupRelatedServerDropdown();
    this.setupDetailsAnimation();
    this.setupInfoModals();
    this.setupTips();
    this.setupFilters();
    this.setupCardSelectors();
    this.setupMemory();
    this.setupMemoryUx();
    this.setupDrives();
    this.setupDrivesUx();
    this.setupEmptyTrays();
    this.setupOsDropdowns();
    this.setupWarranty();
    this.setupWarrantyUx();
    this.setupSectionNotices();
    this.setupPsuWatt();
    this.setupActions();
    this.setupTdpTooltip();
    this.captureDefaultSnapshot();
    this.setupConfigControls();
    this.loadSettings();
    this.recalc();
  }

  // ---------- settings (warranty markups, single source) ----------

  async loadSettings() {
    try {
      const resp = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.dataset.anonKey}`
        },
        body: JSON.stringify({ action: 'settings', chassis_qsku: this.dataset.chassisQsku || undefined })
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok || !Array.isArray(data.warranty_tiers)) {
        throw new Error(data.error || 'settings unavailable');
      }
      this.warrantyTiers = data.warranty_tiers;
      this.trayMap = data.tray && data.tray.map ? data.tray.map : null;
      this.settingsError = null;
    } catch (err) {
      console.error('Quantify settings load failed:', err);
      this.settingsError = String(err.message || err);
      this.warrantyTiers = null;
      this.trayMap = null;
    }
    this.recalc();
  }

  tierMarkup(label) {
    if (!this.warrantyTiers) return null;
    const t = this.warrantyTiers.find((x) => x.label === label);
    return t ? t.markup_percent : null;
  }

  qTrayAdderCents(ff) {
    // Per-drive tray cents for a form factor (tray display amendment,
    // owner ruling Option A, 2026-08-27). Single source for the adder:
    // every tray-inclusive display site calls this, never inline math.
    // Returns 0 when trayMap is null, the ff has no entries, or a tray
    // is unpriced (null cents adds nothing), so display falls back to
    // drive-only and the backend stays fail-loud at Add To Quote.
    if (!this.trayMap) return 0;
    let cents = 0;
    (this.trayMap[ff] || []).forEach((t) => {
      cents += (parseInt(t.unit_cents) || 0) * (t.qty_per_drive || 0);
    });
    return cents;
  }

  // ---------- navigation / layout ----------

  setupRelatedServerDropdown() {
    this.querySelector('select[name="related-server-selection"]')?.addEventListener('change', function () {
      if (this.value) window.location.href = this.value;
    });
  }

  setupDetailsAnimation() {
    this.querySelectorAll('.configurator__section').forEach((section) => {
      const content = section.querySelector('.configurator__content-wrapper');
      let isAnimating = false;
      section.addEventListener('click', (event) => {
        if (!event.target.closest('summary')) return;
        if (event.target.closest('.q-drive-expander')) return;
        if (event.target.closest('.q-info-expander')) return;
        if (event.target.closest('.q-tip-btn')) return;
        event.preventDefault();
        if (isAnimating) return;
        isAnimating = true;
        if (!section.open) {
          section.open = true;
          content.style.height = '0px';
          requestAnimationFrame(() => {
            content.style.height = content.scrollHeight + 'px';
            content.style.opacity = '1';
          });
        } else {
          content.style.height = content.scrollHeight + 'px';
          content.offsetHeight;
          requestAnimationFrame(() => {
            content.style.height = '0px';
            content.style.opacity = '0';
          });
        }
      });
      content.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'height') {
          isAnimating = false;
          if (content.style.height === '0px') section.open = false;
          else content.style.height = 'auto';
        }
      });
    });
  }

  setupInfoModals() {
    this.querySelectorAll('.configurator__info-modal-button').forEach((button) => {
      button.addEventListener('click', () => {
        const modal = document.querySelector(`.configurator__info-modal[data-modal-type="${button.dataset.modalType}"]`);
        if (modal) this.openModal(modal);
      });
    });
    document.querySelectorAll('.configurator__modal-close').forEach((btn) => {
      btn.addEventListener('click', (e) => this.closeModal(e.target.closest('.configurator__modal')));
    });
    document.querySelectorAll('.configurator__modal').forEach((modal) => {
      modal.addEventListener('click', (e) => {
        if (e.target.classList.contains('configurator__modal')) this.closeModal(e.target);
      });
    });
  }

  setupTips() {
    // Wizard redesign 4A: each section head's q-tip-btn toggles that
    // section's q-tip overlay (the rewritten configurator-info-box render).
    // One tip open at a time; opening a tip on a collapsed section opens the
    // section first. Outside clicks close open tips. No-ops on themes
    // without the redesign markup.
    this.querySelectorAll('.q-tip-btn').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const details = btn.closest('details');
        const tip = details ? details.querySelector('.q-tip') : null;
        if (!tip) return;
        const willOpen = !tip.classList.contains('open');
        this.querySelectorAll('.q-tip.open').forEach((t) => t.classList.remove('open'));
        if (willOpen) {
          if (details && !details.open) {
            details.open = true;
            const content = details.querySelector('.configurator__content-wrapper');
            if (content) {
              content.style.height = 'auto';
              content.style.opacity = '1';
            }
          }
          tip.classList.add('open');
        }
      });
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest('.q-tip') || event.target.closest('.q-tip-btn')) return;
      this.querySelectorAll('.q-tip.open').forEach((t) => t.classList.remove('open'));
    });
  }

  openModal(modal) {
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => modal.classList.add('configurator__modal--visible'));
  }

  closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('configurator__modal--visible');
    modal.addEventListener('transitionend', function handler(e) {
      if (e.target === modal && e.propertyName === 'opacity') {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        modal.removeEventListener('transitionend', handler);
      }
    });
  }

  // ---------- filters ----------

  setupFilters() {
    this.querySelectorAll('.configurator__filter-btn').forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.closest('.configurator__filter-group');
        group.querySelectorAll('.configurator__filter-btn').forEach((b) => b.classList.remove('configurator__filter-btn--active'));
        button.classList.add('configurator__filter-btn--active');
        const section = button.closest('.configurator__section');
        this.applySectionFilters(section);
        if (button.dataset.filterField === 'filter_operation_system') {
          this.updateOsSelectVisibility(button.dataset.filterValue);
        }
        if (section?.open) {
          const content = section.querySelector('.configurator__content-wrapper');
          if (content) content.style.height = 'auto';
        }
      });
    });
    // Clear Selection link for the RAM Clock Speed filter (2026-08-10): relocate
    // it into the speed button row so it sits inline with the buttons, then bind.
    // Clicking deactivates the speed filter so every speed's cards render side by
    // side for comparison.
    const clearLink = this.querySelector('.q-clear-speed-filter');
    const speedBtn = this.querySelector('.configurator__filter-btn[data-filter-field="filter_memory_speed"]');
    if (clearLink && speedBtn && speedBtn.parentElement) {
      speedBtn.parentElement.appendChild(clearLink);
      clearLink.style.alignSelf = 'center';
      clearLink.style.justifySelf = 'start';
      clearLink.style.whiteSpace = 'nowrap';
      clearLink.style.margin = '0';
      this.querySelector('.q-clear-speed-filter-wrap')?.remove();
    }
    this.querySelectorAll('.q-clear-speed-filter').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const section = link.closest('.configurator__section');
        if (!section) return;
        section.querySelectorAll('.configurator__filter-btn--active').forEach((b) => {
          if (b.dataset.filterField === 'filter_memory_speed') {
            b.classList.remove('configurator__filter-btn--active');
          }
        });
        this.applySectionFilters(section);
        if (section.open) {
          const content = section.querySelector('.configurator__content-wrapper');
          if (content) content.style.height = 'auto';
        }
      });
    });
    this.querySelectorAll('.configurator__section').forEach((s) => this.applySectionFilters(s));
  }

  activeFilters(section) {
    const filters = {};
    section.querySelectorAll('.configurator__filter-btn--active').forEach((b) => {
      if (b.dataset.filterField && b.dataset.filterValue) filters[b.dataset.filterField] = b.dataset.filterValue;
    });
    return filters;
  }

  applySectionFilters(section) {
    const filters = this.activeFilters(section);
    const fields = Object.keys(filters);
    section.querySelectorAll('.configurator__card').forEach((card) => {
      const relevant = fields.filter((f) => card.dataset[f] !== undefined);
      if (!relevant.length && !card.classList.contains('q-memory-card')) return;
      const matches = relevant.every((f) => card.dataset[f] === filters[f]);
      const extra = card.classList.contains('q-memory-card') ? this.memoryCardMatchesCapacity(card) : true;
      card.style.display = matches && extra ? '' : 'none';
    });
  }

  // ---------- generic card selection ----------

  setupCardSelectors() {
    this.querySelectorAll('.configurator__selector').forEach((selector) => {
      selector.addEventListener('change', (event) => {
        if (!event.target.checked) return;
        const card = event.target.closest('.configurator__card');
        const section = event.target.closest('.configurator__section') || event.target.closest('.configurator__add-ons');
        const scope = section || this;
        scope.querySelectorAll(`input[name="${event.target.name}"]`).forEach((input) => {
          input.closest('.configurator__card')?.classList.remove('configurator__card--selected');
        });
        card?.classList.add('configurator__card--selected');
        this.recalc();
      });
    });
  }

  // ---------- scratch / popular configuration (owner request 2026-08-20) ----------

  captureDefaultSnapshot() {
    // Captures the Liquid-rendered checked state (companion defaults plus the
    // structural defaults: first PSU, default RAID/remote/NIC/OS, base
    // warranty) BEFORE any user interaction. Drives always default to none.
    const radios = {};
    ['processor-selection', 'heatsink-selection', 'memory-selection', 'raid-selection', 'remote-selection', 'power-supply-selection', 'network-card-selection', 'warranty-selection'].forEach((name) => {
      const checked = this.querySelector(`input[name="${name}"]:checked`);
      radios[name] = checked ? checked.value : null;
    });
    this.addonGroupNames().forEach((name) => {
      const checked = this.querySelector(`input[name="${name}"]:checked`);
      radios[name] = checked ? checked.value : null;
    });
    const osValues = [];
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((select) => {
      osValues.push(select.value);
    });
    this.defaultSnapshot = {
      radios,
      osValues,
      warrantyYears: this.querySelector('#q-warranty-years')?.value || '1 Year Warranty'
    };
  }

  setupConfigControls() {
    this.querySelector('[data-start-scratch]')?.addEventListener('click', () => this.startFromScratch());
    this.querySelector('[data-popular-config]')?.addEventListener('click', () => this.restorePopularConfig());
    this.setActiveConfigButton('popular');
    this.addEventListener('change', () => this.setActiveConfigButton(null));
  }

  setActiveConfigButton(which) {
    // 'popular' | 'scratch' | null. Null means the build matches neither
    // baseline (user changed something manually).
    this.querySelector('[data-start-scratch]')?.classList.toggle('q-config-btn--active', which === 'scratch');
    this.querySelector('[data-popular-config]')?.classList.toggle('q-config-btn--active', which === 'popular');
  }

  setRadioValue(name, value) {
    // Sets the checked radio in a group by value (null unchecks all) and keeps
    // card selected classes in sync. Programmatic writes do not fire change
    // events, so callers run recalc() themselves after a batch of writes.
    let matched = null;
    this.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
      const isMatch = value !== null && input.value === value;
      input.checked = isMatch;
      const card = input.closest('.configurator__card');
      if (card) card.classList.toggle('configurator__card--selected', isMatch);
      if (isMatch) matched = input;
    });
    return matched;
  }

  clearDrives() {
    this.selectedDrives.clear();
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      input.value = 0;
      input.closest('.configurator__card')?.classList.remove('configurator__card--selected');
    });
    this.clearEmptyTrays();
    this.refreshDriveUi();
  }

  clearValidationState() {
    // Only clears validation-raised errors (display was set to 'block' by
    // validateSelections); static empty-state notices are left alone.
    this.validationAttempted = false;
    this.querySelectorAll('.configurator__section').forEach((details) => {
      details.style.border = '';
    });
    this.querySelectorAll('.configurator__error-message').forEach((err) => {
      if (err.style.display === 'block') err.style.display = 'none';
    });
  }

  startFromScratch() {
    // Full scratch: chassis kit only. Heatsink returns to the included
    // Standard option and NIC to None (both $0 and both valid selections);
    // warranty returns to the base tier; every other group unchecks and
    // drives clear. Required-section errors surface on Add To Cart / Quote,
    // not here.
    this.setRadioValue('processor-selection', null);
    this.setRadioValue('memory-selection', null);
    this.setRadioValue('raid-selection', null);
    this.setRadioValue('remote-selection', null);
    this.setRadioValue('power-supply-selection', null);
    this.setRadioValue('heatsink-selection', 'standard-heatsink');
    const nicNone = this.setRadioValue('network-card-selection', 'none');
    if (!nicNone) this.setRadioValue('network-card-selection', null);
    this.setRadioValue('warranty-selection', 'limited-warranty');
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((select) => {
      select.selectedIndex = 0;
    });
    this.addonGroupNames().forEach((name) => {
      if (!this.setRadioValue(name, 'none')) this.setRadioValue(name, null);
    });
    this.clearDrives();
    this.clearValidationState();
    this.setActiveConfigButton('scratch');
    this.recalc();
  }

  restorePopularConfig() {
    const snap = this.defaultSnapshot;
    if (!snap) return;
    Object.keys(snap.radios).forEach((name) => this.setRadioValue(name, snap.radios[name]));
    const osSelects = this.querySelectorAll('.configurator__dropdown[name="os-selection"]');
    osSelects.forEach((select, i) => {
      const wanted = snap.osValues[i] || '';
      select.value = wanted;
      if (select.value !== wanted) select.selectedIndex = 0;
    });
    const yearsSelect = this.querySelector('#q-warranty-years');
    if (yearsSelect) yearsSelect.value = snap.warrantyYears;
    this.clearDrives();
    this.clearValidationState();
    this.setActiveConfigButton('popular');
    this.recalc();
  }

  missingSelections() {
    const missing = [];
    if (!this.readRadio('processor-selection')) missing.push('Processor');
    if (!this.selectedMemory()) missing.push('Memory (RAM)');
    if (!this.readRadio('remote-selection')) missing.push('Remote Access');
    if (!this.readRadio('power-supply-selection')) missing.push('Power Supply');
    if (!this.querySelector('input[name="network-card-selection"]:checked')) missing.push('Network Card');
    if (!this.selectedOs()) missing.push('Operating System');
    return missing;
  }

  // ---------- add-ons (section 10, 2026-10-20) ----------

  addonGroupNames() {
    // One radio group per companion add_on_lists entry; the snippet stamps
    // the group name on each ul.q-addon-grid. Empty on legacy markup.
    const names = [];
    this.querySelectorAll('.q-addon-grid').forEach((ul) => {
      if (ul.dataset.addonGroup) names.push(ul.dataset.addonGroup);
    });
    return names;
  }

  selectedAddons() {
    // Real picks only: the None card carries an empty qsku and qty 0, so
    // readRadio returns null for it (same shape as every other reader).
    const picks = [];
    this.addonGroupNames().forEach((name) => {
      const pick = this.readRadio(name);
      if (pick) picks.push(Object.assign({ group: name }, pick));
    });
    return picks;
  }

  // ---------- memory (one radio card per module x count) ----------

  setupMemory() {
    const capFilter = this.querySelector('#q-memory-capacity-filter');
    if (!capFilter) return;
    const totals = new Set();
    this.querySelectorAll('#configurator-memory .q-memory-card').forEach((card) => {
      const gb = parseInt(card.dataset.totalGb) || 0;
      if (gb > 0) totals.add(gb);
    });
    Array.from(totals).sort((a, b) => a - b).forEach((gbTotal) => {
      const opt = document.createElement('option');
      opt.value = String(gbTotal);
      opt.textContent = this.formatGB(gbTotal);
      capFilter.appendChild(opt);
    });
    capFilter.addEventListener('change', () => {
      const section = capFilter.closest('.configurator__section');
      this.applySectionFilters(section);
      this.recalc();
      if (section?.open) {
        const content = section.querySelector('.configurator__content-wrapper');
        if (content) content.style.height = 'auto';
      }
    });
  }

  memoryCardMatchesCapacity(card) {
    const capFilter = this.querySelector('#q-memory-capacity-filter');
    if (!capFilter || capFilter.value === 'all') return true;
    return card.dataset.totalGb === capFilter.value;
  }

  selectedMemory() {
    return this.readRadio('memory-selection');
  }

  // ---------- memory UX (wizard redesign 4B: segs + capacity select) ----------

  setupMemoryUx() {
    // Gate on the redesign shell so legacy markup keeps the old capacity
    // dropdown UI; everything below no-ops without #q-pane.
    if (!document.getElementById('q-pane')) return;
    const memGrid = this.querySelector('#configurator-memory');
    if (!memGrid) return;
    const inputs = memGrid.querySelectorAll('.q-memory-card input[name="memory-selection"]');
    if (!inputs.length) return;

    this.qMemModel = [];
    inputs.forEach((input) => {
      const card = input.closest('.q-memory-card');
      const totalGb = parseInt(card?.dataset.totalGb) || 0;
      const qty = parseInt(input.dataset.qty) || 0;
      if (!card || totalGb <= 0 || qty <= 0) return;
      this.qMemModel.push({
        input,
        value: input.value,
        speedHandle: input.dataset.filter_memory_speed || '',
        totalGb,
        qty,
        unitCents: parseInt(input.dataset.unitCents) || 0,
        moduleGb: totalGb / qty,
        title: card.querySelector('.configurator__card-title')?.textContent?.trim() || input.value
      });
    });
    if (!this.qMemModel.length) return;

    // Speed display names harvest from the existing filter buttons; fall
    // back to the card title ("128GB DDR4 2400MHz (8x 16GB)" -> middle part).
    const speedNames = {};
    this.querySelectorAll('.configurator__filter-btn[data-filter-field="filter_memory_speed"]').forEach((btn) => {
      const label = btn.querySelector('.configurator__filter-btn-title')?.textContent?.trim() || btn.textContent.trim();
      if (btn.dataset.filterValue) speedNames[btn.dataset.filterValue] = label;
    });
    const speeds = [];
    this.qMemModel.forEach((m) => {
      if (!speeds.includes(m.speedHandle)) speeds.push(m.speedHandle);
      if (!speedNames[m.speedHandle]) {
        const t = m.title;
        const sp = t.indexOf(' ');
        const cut = t.lastIndexOf(' (');
        speedNames[m.speedHandle] = (sp > -1 && cut > sp) ? t.slice(sp + 1, cut) : (m.speedHandle || 'Standard');
      }
    });
    this.qMemSpeedNames = speedNames;
    this.qMemSpeeds = speeds;

    // Mount the synthesized UI at the top of the memory options column.
    const optionsCol = memGrid.closest('.configurator__options');
    if (!optionsCol) return;
    const ux = document.createElement('div');
    ux.className = 'q-mem-ux';
    ux.innerHTML = '<div class="q-mem-seg" data-q-mem-speed><span class="q-mem-lab">Speed</span></div><div class="q-mem-seg" data-q-mem-size><span class="q-mem-lab">Module Size</span></div><select class="q-memselect" id="q-mem-select" aria-label="Total memory capacity"></select><div class="q-memchosen" id="q-mem-chosen" style="display:none"></div>';
    optionsCol.insertBefore(ux, optionsCol.firstElementChild);

    const speedRow = ux.querySelector('[data-q-mem-speed]');
    speeds.forEach((s) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.qMemSpeed = s;
      btn.textContent = speedNames[s];
      btn.addEventListener('click', () => this.qMemSetView('speed', s));
      speedRow.appendChild(btn);
    });
    if (speeds.length < 2) speedRow.style.display = 'none';

    ux.querySelector('#q-mem-select').addEventListener('change', (event) => {
      const entry = this.qMemModel.find((m) => m.value === event.target.value);
      if (!entry) return;
      this.qMemLastSel = entry.value;
      entry.input.checked = true;
      entry.input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Initial view: the checked radio's speed/size, else the Liquid default
    // speed filter, else the first model entry.
    const cur = this.qMemCurrent();
    if (cur) {
      this.qMemSpeed = cur.speedHandle;
      this.qMemSize = cur.moduleGb;
      this.qMemLastSel = cur.value;
    } else {
      const activeBtn = this.querySelector('.configurator__filter-btn--active[data-filter-field="filter_memory_speed"]');
      this.qMemSpeed = (activeBtn && speeds.includes(activeBtn.dataset.filterValue)) ? activeBtn.dataset.filterValue : speeds[0];
      this.qMemSize = this.qMemSizesFor(this.qMemSpeed)[0] || 0;
    }

    // Hide the legacy presentation only now that the mount succeeded; the
    // hidden radios remain the state carriers and validation's error message
    // element stays visible-capable below the hidden grid.
    const capFilter = this.querySelector('#q-memory-capacity-filter');
    const capWrap = capFilter ? capFilter.closest('.configurator__dropdown-wrapper') : null;
    if (capWrap) {
      capWrap.classList.add('q-mem-hidden');
      if (capWrap.previousElementSibling?.classList.contains('configurator__label')) {
        capWrap.previousElementSibling.classList.add('q-mem-hidden');
      }
    }
    this.querySelector('.configurator__filter-btn[data-filter-field="filter_memory_speed"]')?.closest('.configurator__filter-group')?.classList.add('q-mem-hidden');
    this.querySelector('.q-clear-speed-filter-wrap')?.classList.add('q-mem-hidden');
    if (memGrid.previousElementSibling?.classList.contains('configurator__label')) {
      memGrid.previousElementSibling.classList.add('q-mem-hidden');
    }
    memGrid.classList.add('q-mem-hidden');

    this.qMemUxReady = true;
  }

  qMemCurrent() {
    const checked = this.querySelector('input[name="memory-selection"]:checked');
    if (!checked) return null;
    return this.qMemModel.find((m) => m.input === checked) || null;
  }

  qMemSizesFor(speedHandle) {
    const sizes = [];
    this.qMemModel.forEach((m) => {
      if (m.speedHandle === speedHandle && !sizes.includes(m.moduleGb)) sizes.push(m.moduleGb);
    });
    return sizes.sort((a, b) => a - b);
  }

  qMemSetView(kind, value) {
    // Speed / Module Size seg click. Mock behavior: the current DIMM count
    // migrates to the new list when the same count exists there (honest
    // price update through the normal change flow); otherwise the existing
    // selection is kept unchanged, never silently cleared.
    if (kind === 'speed') {
      if (value === this.qMemSpeed) return;
      this.qMemSpeed = value;
      const sizes = this.qMemSizesFor(value);
      if (!sizes.includes(this.qMemSize)) this.qMemSize = sizes[0] || 0;
    } else {
      if (value === this.qMemSize) return;
      this.qMemSize = value;
    }
    const cur = this.qMemCurrent();
    if (cur) {
      const target = this.qMemModel.find((m) => m.speedHandle === this.qMemSpeed && m.moduleGb === this.qMemSize && m.qty === cur.qty);
      if (target && target !== cur) {
        this.qMemLastSel = target.value;
        target.input.checked = true;
        target.input.dispatchEvent(new Event('change', { bubbles: true }));
        return; // the change event runs recalc, which re-renders this UX
      }
    }
    this.recalc();
  }

  renderMemoryUx() {
    if (!this.qMemUxReady) return;
    const ux = this.querySelector('.q-mem-ux');
    if (!ux) return;
    const cur = this.qMemCurrent();
    // Snap the view to a NEW selection (Popular restore, scratch defaults);
    // plain browsing without a selection change never yanks the view back.
    if (cur && cur.value !== this.qMemLastSel) {
      this.qMemSpeed = cur.speedHandle;
      this.qMemSize = cur.moduleGb;
      this.qMemLastSel = cur.value;
    } else if (!cur) {
      this.qMemLastSel = null;
    }
    const sizes = this.qMemSizesFor(this.qMemSpeed);
    if (!sizes.includes(this.qMemSize)) this.qMemSize = sizes[0] || 0;

    ux.querySelectorAll('[data-q-mem-speed] button').forEach((btn) => {
      btn.classList.toggle('on', btn.dataset.qMemSpeed === this.qMemSpeed);
    });

    const sizeRow = ux.querySelector('[data-q-mem-size]');
    sizeRow.querySelectorAll('button').forEach((b) => b.remove());
    sizes.forEach((gb) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.classList.toggle('on', gb === this.qMemSize);
      btn.innerHTML = `${gb}GB<small>per DIMM</small>`;
      btn.addEventListener('click', () => this.qMemSetView('size', gb));
      sizeRow.appendChild(btn);
    });

    // ONE select of total capacities; tails carry difference pricing against
    // the ACTUAL current selection (same delta source as the card grid), and
    // re-render on every recalc pass. The tail reads ", Price: $x" because a
    // bare hyphen before the amount looked like a negative number (owner
    // request 2026-08-26).
    const select = ux.querySelector('#q-mem-select');
    const entries = this.qMemModel.filter((m) => m.speedHandle === this.qMemSpeed && m.moduleGb === this.qMemSize).sort((a, b) => a.totalGb - b.totalGb);
    const selLine = cur ? cur.unitCents * cur.qty : null;
    const curInView = !!cur && entries.indexOf(cur) !== -1;
    select.innerHTML = '';
    if (!curInView) {
      const ph = document.createElement('option');
      ph.value = '';
      ph.textContent = 'Select total memory capacity...';
      ph.disabled = true;
      ph.selected = true;
      select.appendChild(ph);
    }
    entries.forEach((e) => {
      const p = e.unitCents * e.qty;
      let tail;
      if (cur && e === cur) {
        tail = `${this.formatMoney(p)} (selected)`;
      } else if (selLine === null) {
        tail = this.formatMoney(p);
      } else {
        const d = p - selLine;
        tail = d === 0 ? `${this.formatMoney(p)} (same price)` : `${this.formatMoney(p)} (${d > 0 ? '+' : '-'}${this.formatMoney(Math.abs(d))})`;
      }
      const opt = document.createElement('option');
      opt.value = e.value;
      opt.textContent = `${e.title}, Price: ${tail}`;
      if (cur && e === cur) opt.selected = true;
      select.appendChild(opt);
    });

    // Populates line describes the actual selection wherever the view sits.
    const chosen = ux.querySelector('#q-mem-chosen');
    if (cur) {
      const slots = parseInt(this.dataset.dimmSlots) || 0;
      const slotsPart = slots > 0 ? `Populates <b>${cur.qty}</b> of <b>${slots}</b> DIMM slots` : `Populates <b>${cur.qty}</b> DIMM slots`;
      chosen.innerHTML = `${slotsPart} &middot; <b>${this.formatMoney(cur.unitCents)}</b> per module &middot; kit total <b>${this.formatMoney(cur.unitCents * cur.qty)}</b>`;
      chosen.style.display = '';
    } else {
      chosen.style.display = 'none';
    }
  }

  // ---------- drives ----------

  setupDrives() {
    this.querySelectorAll('.q-drive-expander').forEach((expander) => {
      const summary = expander.querySelector('summary');
      const label = expander.dataset.groupLabel;
      if (!summary || !label) return;
      expander.addEventListener('toggle', () => {
        summary.textContent = `${expander.open ? 'Hide' : 'Show'} ${label}`;
      });
    });
    this.querySelectorAll('.q-drive-group .quantity__button').forEach((button) => {
      button.addEventListener('click', () => {
        const input = button.parentElement.querySelector('.quantity__input');
        const cur = parseInt(input.value) || 0;
        input.value = button.name === 'plus' ? cur + 1 : cur - 1;
        this.applyDriveQty(input);
      });
    });
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      input.addEventListener('change', () => this.applyDriveQty(input));
      input.addEventListener('wheel', (e) => e.preventDefault());
    });
  }

  setupDriveGroupLimits() {
    this.qGroupLimits = new Map();
    try {
      const raw = JSON.parse(this.dataset.driveGroupLimits || 'null');
      if (Array.isArray(raw)) {
        raw.forEach((entry) => {
          const s = String(entry);
          const i = s.lastIndexOf('|');
          if (i <= 0) return;
          const max = parseInt(s.slice(i + 1)) || 0;
          if (max > 0) this.qGroupLimits.set(s.slice(0, i), max);
        });
      }
    } catch (_) { /* no limits */ }
    this.qDriveGroupOf = new Map();
    this.querySelectorAll('.q-drive-expander[data-group-gid] .quantity__input[data-drive-qsku]').forEach((input) => {
      const gid = input.closest('.q-drive-expander')?.dataset.groupGid;
      const qsku = input.dataset.driveQsku;
      if (gid && qsku && !this.qDriveGroupOf.has(qsku)) this.qDriveGroupOf.set(qsku, gid);
    });
  }

  qGroupLimitFor(qsku) {
    const gid = this.qDriveGroupOf.get(qsku);
    if (!gid || !this.qGroupLimits.has(gid)) return null;
    return { gid, max: this.qGroupLimits.get(gid) };
  }

  qGroupUsed(gid) {
    let used = 0;
    this.selectedDrives.forEach((d, qsku) => { if (this.qDriveGroupOf.get(qsku) === gid) used += d.qty; });
    return used;
  }

  // Drives of this qsku's group still allowed, counting the qsku's own qty
  // as available (same shape as the bay "remaining" in applyDriveQty).
  qGroupRemaining(qsku) {
    const lim = this.qGroupLimitFor(qsku);
    if (!lim) return Infinity;
    const current = this.selectedDrives.get(qsku)?.qty || 0;
    return lim.max - this.qGroupUsed(lim.gid) + current;
  }

  usedDriveSlots() {
    let used = 0;
    this.selectedDrives.forEach((d) => { used += d.qty; });
    return used;
  }

  applyDriveQty(input) {
    const qsku = input.dataset.driveQsku;
    const current = this.selectedDrives.get(qsku)?.qty || 0;
    const remaining = this.maxDriveSlots - this.usedDriveSlots() + current;
    let wanted = parseInt(input.value) || 0;
    wanted = Math.max(0, Math.min(wanted, remaining, this.qGroupRemaining(qsku)));
    input.value = wanted;
    const card = input.closest('.configurator__card');
    if (wanted === 0) {
      this.selectedDrives.delete(qsku);
      card?.classList.remove('configurator__card--selected');
    } else {
      this.selectedDrives.set(qsku, {
        qty: wanted,
        unitCents: parseInt(input.dataset.unitCents) || 0,
        ff: input.dataset.driveFf,
        title: input.dataset.title
      });
      card?.classList.add('configurator__card--selected');
    }
    this.refreshDriveUi();
    this.recalc();
  }

  refreshDriveUi() {
    const used = this.usedDriveSlots();
    const counter = this.querySelector('.configurator__slots-used');
    if (counter) counter.textContent = used;
    const remaining = this.maxDriveSlots - used;
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      const qty = this.selectedDrives.get(input.dataset.driveQsku)?.qty || 0;
      const groupMore = this.qGroupRemaining(input.dataset.driveQsku) - qty;
      input.max = qty + Math.max(0, Math.min(remaining, groupMore));
      const plusBtn = input.parentElement.querySelector('button[name="plus"]');
      if (plusBtn) plusBtn.disabled = remaining <= 0 || groupMore <= 0;
    });
  }

  // ---------- drives UX (wizard redesign 4C: pinned selection + type seg) ----------

  setupDrivesUx() {
    // Gate on the redesign shell; legacy markup keeps the Show/Hide expanders.
    if (!document.getElementById('q-pane')) return;
    const section = this.querySelector('.configurator__drives-section');
    if (!section) return;
    const optionsCol = section.querySelector('.configurator__options');
    if (!optionsCol) return;

    // Group census: Recommended (the grid outside any expander) first, then
    // one entry per q-drive-expander in DOM order.
    this.qDrvGroups = [];
    const recGrid = Array.from(section.querySelectorAll('.q-drive-group')).find((g) => !g.closest('.q-drive-expander'));
    if (recGrid) {
      this.qDrvGroups.push({ key: 'rec', label: 'Recommended', el: recGrid.closest('.configurator__subsection') || recGrid });
    }
    section.querySelectorAll('.q-drive-expander').forEach((exp, i) => {
      this.qDrvGroups.push({ key: 'g' + i, label: exp.dataset.groupLabel || 'Drives', el: exp, expander: true });
    });
    if (!this.qDrvGroups.length) return;

    // Mount: pinned selection box and Type seg above the groups (the seg
    // reuses the q-mem-seg pill styles), bayline before the tray notice.
    const ux = document.createElement('div');
    ux.className = 'q-drv-ux';
    const selBox = document.createElement('div');
    selBox.className = 'q-seldrives';
    selBox.style.display = 'none';
    const sdHead = document.createElement('div');
    sdHead.className = 'q-sd-head';
    const sdRows = document.createElement('div');
    selBox.appendChild(sdHead);
    selBox.appendChild(sdRows);
    this.qDrvSelBox = selBox;
    this.qDrvSelHead = sdHead;
    this.qDrvSelRows = sdRows;
    const seg = document.createElement('div');
    seg.className = 'q-mem-seg q-drv-seg';
    const lab = document.createElement('span');
    lab.className = 'q-mem-lab';
    lab.textContent = 'Type';
    seg.appendChild(lab);
    this.qDrvGroups.forEach((g) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.qDrvGroup = g.key;
      btn.textContent = g.label;
      btn.addEventListener('click', () => this.qDrvSetGroup(g.key));
      seg.appendChild(btn);
    });
    if (this.qDrvGroups.length < 2) seg.style.display = 'none';
    ux.appendChild(selBox);
    ux.appendChild(seg);
    optionsCol.insertBefore(ux, optionsCol.firstElementChild);

    const bayline = document.createElement('div');
    bayline.className = 'q-bayline';
    this.qDrvBayline = bayline;
    // Empty drive trays (2026-10-22): the bayline sits before the empties card
    // when the card is rendered, so the order reads groups, bayline, empties,
    // notice.
    const emptyCard = optionsCol.querySelector('[data-empty-tray]');
    const notice = optionsCol.querySelector('.configurator__info-notice');
    if (emptyCard) optionsCol.insertBefore(bayline, emptyCard);
    else if (notice) optionsCol.insertBefore(bayline, notice);
    else optionsCol.appendChild(bayline);

    // Expander chrome retires: summaries hide, the active expander is forced
    // open. The Recommended label is redundant with the seg and hides too.
    // Markup stays in the snippet untouched for easy revert.
    section.querySelectorAll('.q-drive-expander > summary').forEach((s) => s.classList.add('q-mem-hidden'));
    if (recGrid) {
      const recWrap = recGrid.closest('.configurator__subsection');
      const recLabel = recWrap ? recWrap.querySelector('.configurator__label') : null;
      if (recLabel) recLabel.classList.add('q-mem-hidden');
    }

    // Selection box actions delegate once (stepper plus remove); all writes
    // route through qDrvSetQty into applyDriveQty (single clamp source).
    selBox.addEventListener('click', (event) => {
      const btn = event.target.closest('button[data-q-sd-act]');
      if (!btn) return;
      const qsku = btn.dataset.qSdQsku;
      const cur = this.selectedDrives.get(qsku)?.qty || 0;
      if (btn.dataset.qSdAct === 'plus') this.qDrvSetQty(qsku, cur + 1);
      else if (btn.dataset.qSdAct === 'minus') this.qDrvSetQty(qsku, cur - 1);
      else this.qDrvSetQty(qsku, 0);
    });

    this.qDrvGroup = this.qDrvGroups[0].key;
    this.qDrvApplyVisibility();
    this.qDrvUxReady = true;
  }

  qDrvSetGroup(key) {
    if (key === this.qDrvGroup) return;
    this.qDrvGroup = key;
    this.qDrvApplyVisibility();
  }

  qDrvApplyVisibility() {
    this.qDrvGroups.forEach((g) => {
      const active = g.key === this.qDrvGroup;
      g.el.classList.toggle('q-mem-hidden', !active);
      if (active && g.expander) g.el.open = true;
    });
    this.querySelectorAll('.q-drv-seg button').forEach((btn) => {
      btn.classList.toggle('on', btn.dataset.qDrvGroup === this.qDrvGroup);
    });
    const section = this.querySelector('.configurator__drives-section');
    if (section?.open) {
      const content = section.querySelector('.configurator__content-wrapper');
      if (content) content.style.height = 'auto';
    }
  }

  qDrvSetQty(qsku, qty) {
    // Route through applyDriveQty so the bay clamp and selectedDrives stay
    // the single source; renderDrivesUx then mirrors the result to every
    // twin input sharing the qsku.
    const input = this.querySelector(`.q-drive-group .quantity__input[data-drive-qsku="${qsku}"]`);
    if (!input) return;
    input.value = Math.max(0, qty);
    this.applyDriveQty(input);
  }

  renderDrivesUx() {
    if (!this.qDrvUxReady) return;
    const used = this.usedDriveSlots();
    const max = this.maxDriveSlots;
    const atMax = max > 0 && used >= max;

    // Twin sync: every stepper input and card mirrors selectedDrives (the
    // single source), so the Recommended and home-group copies of one qsku
    // can never disagree on screen.
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      const qty = this.selectedDrives.get(input.dataset.driveQsku)?.qty || 0;
      if ((parseInt(input.value) || 0) !== qty) input.value = qty;
      input.closest('.configurator__card')?.classList.toggle('configurator__card--selected', qty > 0);
    });

    // Pinned selection box.
    if (this.selectedDrives.size) {
      let anyTrayAdder = false;
      this.selectedDrives.forEach((d) => { if (this.qTrayAdderCents(d.ff) > 0) anyTrayAdder = true; });
      this.qDrvSelHead.textContent = `Your Drives · ${used} of ${max} bays` + (anyTrayAdder ? ' · prices incl. trays' : '');
      this.qDrvSelRows.textContent = '';
      this.selectedDrives.forEach((d, qsku) => {
        const row = document.createElement('div');
        row.className = 'q-sd-row';
        const qty = document.createElement('span');
        qty.className = 'q-sd-qty';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.dataset.qSdAct = 'minus';
        minus.dataset.qSdQsku = qsku;
        minus.textContent = '−';
        const num = document.createElement('b');
        num.textContent = d.qty;
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.dataset.qSdAct = 'plus';
        plus.dataset.qSdQsku = qsku;
        plus.textContent = '+';
        plus.disabled = atMax || (this.qGroupRemaining(qsku) - d.qty) <= 0;
        qty.appendChild(minus);
        qty.appendChild(num);
        qty.appendChild(plus);
        const name = document.createElement('span');
        name.className = 'q-sd-name';
        name.textContent = d.title || qsku;
        const spring = document.createElement('span');
        spring.className = 'q-sd-spring';
        const amt = document.createElement('span');
        amt.className = 'q-sd-amt';
        // Tray display amendment 2026-08-27: row amount matches the
        // card math (drive plus tray per unit, times qty).
        amt.textContent = this.formatMoney((d.unitCents + this.qTrayAdderCents(d.ff)) * d.qty);
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'q-sd-rm';
        rm.dataset.qSdAct = 'rm';
        rm.dataset.qSdQsku = qsku;
        rm.title = 'Remove';
        rm.textContent = '×';
        row.appendChild(qty);
        row.appendChild(name);
        row.appendChild(spring);
        row.appendChild(amt);
        row.appendChild(rm);
        this.qDrvSelRows.appendChild(row);
      });
      this.qDrvSelBox.style.display = '';
    } else {
      this.qDrvSelBox.style.display = 'none';
    }

    // Bayline with the chassis-limit chip at max, plus one line per capped
    // drive group (drive group cap, 2026-10-21).
    let groupLines = '';
    this.qGroupLimits.forEach((gmax, gid) => {
      const grp = this.qDrvGroups.find((g) => g.expander && g.el.dataset.groupGid === gid);
      const label = grp ? grp.label : 'Drives';
      const usedG = this.qGroupUsed(gid);
      groupLines += `<div class="q-bayline-group">${label}: <b>${usedG}</b> of ${gmax}` + (usedG >= gmax ? `<span class="q-limitmsg">All ${gmax} ${label} bays filled, chassis limit reached</span>` : '') + '</div>';
    });
    this.qDrvBayline.innerHTML = `<b>${used}</b> of ${max} bays used` + (atMax ? `<span class="q-limitmsg">All ${max} bays filled, chassis limit reached</span>` : '') + groupLines;
  }

  // ---------- empty drive trays (owner go 2026-10-22, R-ET-1..5) ----------

  emptyTrayInput() {
    return this.querySelector('.q-empty-tray-input');
  }

  emptyTrayInfo() {
    // Native tray card data rendered by the snippet from the companion
    // empty_tray field. Null when the card is absent. qty mirrors
    // this.emptyTrayQty (the single source).
    const input = this.emptyTrayInput();
    if (!input) return null;
    return {
      qsku: input.dataset.emptyTrayQsku || '',
      unitCents: parseInt(input.dataset.unitCents) || 0,
      title: input.dataset.title || 'Empty Drive Tray',
      qty: this.emptyTrayQty
    };
  }

  setupEmptyTrays() {
    const input = this.emptyTrayInput();
    if (!input) return;
    input.parentElement.querySelectorAll('.quantity__button').forEach((button) => {
      button.addEventListener('click', () => {
        const cur = parseInt(input.value) || 0;
        input.value = button.name === 'plus' ? cur + 1 : cur - 1;
        this.applyEmptyTrayQty(input);
      });
    });
    input.addEventListener('change', () => this.applyEmptyTrayQty(input));
    input.addEventListener('wheel', (e) => e.preventDefault());
  }

  applyEmptyTrayQty(input) {
    // Loose quantity (R-ET-2): never counts toward bays or group caps. The
    // sanity ceiling 99 matches the backend validation.
    let wanted = parseInt(input.value) || 0;
    wanted = Math.max(0, Math.min(99, wanted));
    input.value = wanted;
    this.emptyTrayQty = wanted;
    input.closest('.configurator__card')?.classList.toggle('configurator__card--selected', wanted > 0);
    this.recalc();
  }

  clearEmptyTrays() {
    this.emptyTrayQty = 0;
    const input = this.emptyTrayInput();
    if (!input) return;
    input.value = 0;
    input.closest('.configurator__card')?.classList.remove('configurator__card--selected');
  }

  renderEmptyTray() {
    const priceEl = this.querySelector('.q-empty-tray-price');
    const et = this.emptyTrayInfo();
    if (!priceEl || !et) return;
    priceEl.textContent = `+${this.formatMoney(et.qty > 0 ? et.unitCents * et.qty : et.unitCents)}`;
  }

  // ---------- OS ----------

  setupOsDropdowns() {
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((select) => {
      select.addEventListener('change', () => {
        this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((other) => {
          if (other !== select) other.selectedIndex = 0;
        });
        const errorMessage = select.closest('.configurator__section')?.querySelector('.configurator__error-message');
        if (errorMessage) errorMessage.style.display = select.value ? 'none' : 'block';
        this.recalc();
      });
    });
  }

  updateOsSelectVisibility(selectedType) {
    const handleized = selectedType.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    this.querySelectorAll('.configurator__dropdown-wrapper[data-os-type]').forEach((wrapper) => {
      wrapper.style.display = wrapper.dataset.osType === handleized ? '' : 'none';
    });
  }

  selectedOs() {
    for (const select of this.querySelectorAll('.configurator__dropdown[name="os-selection"]')) {
      const opt = select.options[select.selectedIndex];
      if (opt && opt.value !== '') {
        return {
          qsku: opt.dataset.qsku,
          unitCents: parseInt(opt.dataset.unitCents) || 0,
          title: opt.textContent.trim()
        };
      }
    }
    return null;
  }

  // ---------- warranty ----------

  setupWarranty() {
    const yearsSelect = this.querySelector('#q-warranty-years');
    if (yearsSelect) {
      yearsSelect.addEventListener('click', (e) => e.stopPropagation());
      yearsSelect.addEventListener('change', () => {
        const smsRadio = this.querySelector('input[name="warranty-selection"][value="sms-warranty"]');
        if (smsRadio && !smsRadio.checked) {
          smsRadio.checked = true;
          smsRadio.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.recalc();
        }
      });
    }
  }

  selectedWarrantyLabel() {
    const checked = this.querySelector('input[name="warranty-selection"]:checked');
    if (!checked || checked.value !== 'sms-warranty') return '180 days Warranty';
    const yearsSelect = this.querySelector('#q-warranty-years');
    return yearsSelect?.value || '180 days Warranty';
  }

  // ---------- warranty UX (wizard redesign 4D: tier buttons + description) ----------

  setupWarrantyUx() {
    // Gate on the redesign shell; legacy markup keeps the radio cards.
    if (!document.getElementById('q-pane')) return;
    const data = document.getElementById('q-warr-data');
    const addOns = this.querySelector('.configurator__add-ons');
    if (!data || !addOns) return;
    const grid = addOns.querySelector('.configurator__grid');
    if (!grid) return;
    this.qWarrData = [];
    data.querySelectorAll('[data-years]').forEach((el) => {
      this.qWarrData.push({ years: parseInt(el.dataset.years) || 0, desc: el.textContent.trim() });
    });

    const wrap = document.createElement('div');
    wrap.className = 'q-warr-ux';
    const row = document.createElement('div');
    row.className = 'q-mem-seg q-warr-row';
    const lab = document.createElement('span');
    lab.className = 'q-mem-lab';
    lab.textContent = 'Coverage';
    row.appendChild(lab);
    [{ years: 0, label: '180-Day', tier: '180 days Warranty' },
      { years: 1, label: '1-Year', tier: '1 Year Warranty' },
      { years: 2, label: '2-Year', tier: '2 Years Warranty' },
      { years: 3, label: '3-Year', tier: '3 Years Warranty' }].forEach((t) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.qWarrTier = t.tier;
      btn.dataset.qWarrYears = t.years;
      btn.dataset.qWarrBtnLabel = t.label; // Btn infix keeps the attribute clear of the pane's [data-q-warr-label] selector
      btn.innerHTML = `${t.label}<small>...</small>`;
      btn.addEventListener('click', () => this.qWarrSetTier(t));
      row.appendChild(btn);
    });
    const desc = document.createElement('div');
    desc.className = 'q-notice q-warr-desc';
    desc.style.display = 'none';
    wrap.appendChild(row);
    wrap.appendChild(desc);
    grid.parentElement.insertBefore(wrap, grid);
    grid.classList.add('q-mem-hidden');
    this.qWarrRow = row;
    this.qWarrDesc = desc;
    this.qWarrUxReady = true;
  }

  qWarrSetTier(t) {
    // Buttons drive the existing state carriers: the base tier checks the
    // limited-warranty radio; paid tiers set the hidden years select and
    // check the sms-warranty radio, flowing through the same handlers.
    if (t.years === 0) {
      const base = this.querySelector('input[name="warranty-selection"][value="limited-warranty"]');
      if (!base) return;
      if (!base.checked) {
        base.checked = true;
        base.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.recalc();
      }
      return;
    }
    const yearsSelect = this.querySelector('#q-warranty-years');
    const sms = this.querySelector('input[name="warranty-selection"][value="sms-warranty"]');
    if (!yearsSelect || !sms) return;
    yearsSelect.value = t.tier;
    if (!sms.checked) {
      sms.checked = true;
      sms.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.recalc();
    }
  }

  renderWarrantyUx(base) {
    if (!this.qWarrUxReady) return;
    const selected = this.selectedWarrantyLabel();
    let selYears = 0;
    this.qWarrRow.querySelectorAll('button[data-q-warr-tier]').forEach((btn) => {
      const markup = this.tierMarkup(btn.dataset.qWarrTier);
      let amt;
      if (markup === 0) amt = 'included';
      else if (markup === null) amt = this.settingsError ? 'unavailable' : '...';
      else amt = '+' + this.formatMoney(Math.round(base * markup / 100));
      btn.innerHTML = `${btn.dataset.qWarrBtnLabel}<small>${amt}</small>`;
      const on = btn.dataset.qWarrTier === selected;
      btn.classList.toggle('on', on);
      if (on) selYears = parseInt(btn.dataset.qWarrYears) || 0;
    });
    const entry = this.qWarrData.find((w) => w.years === selYears);
    if (entry && entry.desc) {
      this.qWarrDesc.innerHTML = `<b>${selYears === 0 ? '180-Day' : selYears + '-Year'} Warranty.</b> `;
      this.qWarrDesc.appendChild(document.createTextNode(entry.desc));
      this.qWarrDesc.style.display = '';
    } else {
      this.qWarrDesc.style.display = 'none';
    }
  }

  // ---------- section notices (wizard redesign 4D: included defaults) ----------

  setupSectionNotices() {
    if (!document.getElementById('q-pane')) return;
    const hsInput = this.querySelector('input[name="heatsink-selection"]');
    const hsGrid = hsInput ? hsInput.closest('.configurator__grid') : null;
    if (hsGrid && !this.querySelector('[data-q-hs-flag]')) {
      const n = document.createElement('div');
      n.className = 'q-notice';
      n.innerHTML = '<b>Standard Heatsink/Fan</b><span class="q-autoflag" data-q-hs-flag>AUTO-SELECTED</span> included with the chassis; upgrades below are optional.';
      hsGrid.parentElement.insertBefore(n, hsGrid);
    }
    const nicInput = this.querySelector('input[name="network-card-selection"]');
    const nicGrid = nicInput ? nicInput.closest('.configurator__grid') : null;
    if (nicGrid && !nicGrid.parentElement.querySelector('.q-notice')) {
      const n = document.createElement('div');
      n.className = 'q-notice';
      n.innerHTML = '<b>Onboard networking included.</b> Add-on network cards below are optional.';
      nicGrid.parentElement.insertBefore(n, nicGrid);
    }
  }

  // ---------- PSU wattline (4D punch 2: mock v11 watt bar port) ----------

  setupPsuWatt() {
    // Gate on the redesign shell. Rating parses from the selected PSU card
    // title (e.g. "2x 1100W R3 Power Supply" reads 1100W, per single PSU as
    // in the mock); with no parseable wattage the bar stays hidden.
    if (!document.getElementById('q-pane')) return;
    const grid = this.querySelector('#configurator-power-supplies');
    if (!grid || this.querySelector('[data-q-wattline]')) return;
    const el = document.createElement('div');
    el.className = 'wattline';
    el.setAttribute('data-q-wattline', '');
    el.style.display = 'none';
    el.innerHTML = 'Estimated TDP Draw <b data-q-wl-draw></b> of <span data-q-wl-cap></span><span class="wattbar"><i data-q-wl-fill></i></span><span data-q-wl-msg style="display:none"></span><span class="q-wl-calcwrap" data-q-wl-calc><button type="button" class="q-wl-calcbtn" aria-label="Show the TDP calculation breakdown">View TDP calculation</button><div class="q-tdp-tip"></div></span>';
    grid.parentElement.insertBefore(el, grid.nextSibling);
    // 4D punch 3: the calc wrap opens the same per-section breakdown window
    // the summary watts tooltip shows. The tip carries class q-tdp-tip so
    // updateTdpTooltip() fills it on every recalc (one fill path); the wrap
    // deliberately does NOT use q-tdp-wrap, which would false-trip the
    // already-mounted guard in setupTdpTooltip() (this runs first in init).
    const calcWrap = el.querySelector('[data-q-wl-calc]');
    if (calcWrap) {
      calcWrap.querySelector('.q-wl-calcbtn').addEventListener('click', (e) => {
        e.stopPropagation();
        calcWrap.classList.toggle('q-tdp-open');
      });
      calcWrap.addEventListener('mouseenter', () => calcWrap.classList.add('q-tdp-open'));
      calcWrap.addEventListener('mouseleave', () => calcWrap.classList.remove('q-tdp-open'));
      document.addEventListener('click', (event) => {
        if (!calcWrap.contains(event.target)) calcWrap.classList.remove('q-tdp-open');
      });
    }
  }

  psuRatingWatts() {
    // Single source for the PSU rating parse (4D punch 3): the wattline and
    // the pane over-rating notice read the same figure. Single PSU rating
    // per the mock, even on 2x kits.
    const psu = this.querySelector('input[name="power-supply-selection"]:checked');
    const title = psu ? (psu.closest('.configurator__card')?.querySelector('.configurator__card-title')?.textContent || '') : '';
    const m = title.match(/(\d{3,4})\s*W/i);
    return m ? parseInt(m[1]) : 0;
  }

  renderPsuWatt(draw) {
    const el = this.querySelector('[data-q-wattline]');
    if (!el) return;
    const rating = this.psuRatingWatts();
    if (!rating) { el.style.display = 'none'; return; }
    const pct = Math.min(100, Math.round((draw / rating) * 100));
    const hot = pct > 80;
    const over = draw > rating;
    el.querySelector('[data-q-wl-draw]').textContent = Math.round(draw) + 'W';
    el.querySelector('[data-q-wl-cap]').textContent = rating + 'W';
    const fill = el.querySelector('[data-q-wl-fill]');
    fill.style.width = pct + '%';
    fill.classList.toggle('hot', hot || over);
    const msg = el.querySelector('[data-q-wl-msg]');
    if (over) {
      msg.className = 'q-overmsg';
      msg.textContent = 'Estimated draw exceeds a single PSU rating; consider a higher wattage option';
      msg.style.display = '';
    } else if (hot) {
      msg.className = 'q-limitmsg';
      msg.textContent = 'Approaching the PSU rating';
      msg.style.display = '';
    } else {
      msg.style.display = 'none';
    }
    el.style.display = '';
  }

  // ---------- selection readers ----------

  readRadio(name) {
    const input = this.querySelector(`input[name="${name}"]:checked`);
    if (!input || !input.dataset.qsku) return null;
    const qty = parseInt(input.dataset.qty) || 0;
    if (qty < 1) return null;
    return {
      qsku: input.dataset.qsku,
      qty,
      unitCents: parseInt(input.dataset.unitCents) || 0,
      title: input.closest('.configurator__card')?.querySelector('.configurator__card-title')?.textContent?.trim() || input.dataset.qsku
    };
  }

  buildLines() {
    const lines = [];
    lines.push({ sku: this.dataset.chassisQsku, qty: 1, role: 'chassis' });
    const proc = this.readRadio('processor-selection');
    if (proc) lines.push({ sku: proc.qsku, qty: proc.qty, role: 'processor' });
    const heatsink = this.readRadio('heatsink-selection');
    if (heatsink) lines.push({ sku: heatsink.qsku, qty: heatsink.qty, role: 'heatsink' });
    const memory = this.selectedMemory();
    if (memory) lines.push({ sku: memory.qsku, qty: memory.qty, role: 'memory' });
    const raid = this.readRadio('raid-selection');
    if (raid) lines.push({ sku: raid.qsku, qty: raid.qty, role: 'raid' });
    this.selectedDrives.forEach((d, qsku) => {
      lines.push({ sku: qsku, qty: d.qty, role: 'drive', drive_ff: d.ff });
    });
    const remote = this.readRadio('remote-selection');
    if (remote) lines.push({ sku: remote.qsku, qty: remote.qty, role: 'remote' });
    const psu = this.readRadio('power-supply-selection');
    if (psu) lines.push({ sku: psu.qsku, qty: psu.qty, role: 'psu' });
    const nic = this.readRadio('network-card-selection');
    if (nic) lines.push({ sku: nic.qsku, qty: nic.qty, role: 'nic' });
    const os = this.selectedOs();
    if (os) lines.push({ sku: os.qsku, qty: 1, role: 'os' });
    this.selectedAddons().forEach((a) => {
      lines.push({ sku: a.qsku, qty: a.qty, role: 'addon' });
    });
    return lines;
  }

  // ---------- pricing (page prices; backend total is authoritative) ----------

  baseTotalCents() {
    let total = parseInt(this.dataset.chassisCents) || 0;
    const proc = this.readRadio('processor-selection');
    if (proc) total += proc.unitCents * proc.qty;
    const heatsink = this.readRadio('heatsink-selection');
    if (heatsink) total += heatsink.unitCents * heatsink.qty;
    const memory = this.selectedMemory();
    if (memory) total += memory.unitCents * memory.qty;
    const raid = this.readRadio('raid-selection');
    if (raid) total += raid.unitCents * raid.qty;
    this.selectedDrives.forEach((d) => { total += d.unitCents * d.qty; });
    // Tray cost (2026-08-26): mirror resolve_tray so the pre-quote total
    // matches the backend quote total. A null unit_cents adds nothing here;
    // the backend blocks quote creation on unpriced trays, so it stays
    // authoritative and fail-loud at Add To Quote.
    if (this.trayMap) {
      this.selectedDrives.forEach((d) => {
        (this.trayMap[d.ff] || []).forEach((t) => {
          total += (parseInt(t.unit_cents) || 0) * (t.qty_per_drive || 0) * d.qty;
        });
      });
    }
    // Empty drive trays (2026-10-22): loose quantity at the native tray's
    // unit price, separate from the per-drive tray block above.
    {
      const et = this.emptyTrayInfo();
      if (et && et.qty > 0) total += et.unitCents * et.qty;
    }
    const remote = this.readRadio('remote-selection');
    if (remote) total += remote.unitCents * remote.qty;
    const psu = this.readRadio('power-supply-selection');
    if (psu) total += psu.unitCents * psu.qty;
    const nic = this.readRadio('network-card-selection');
    if (nic) total += nic.unitCents * nic.qty;
    const os = this.selectedOs();
    if (os) total += os.unitCents;
    this.selectedAddons().forEach((a) => { total += a.unitCents * a.qty; });
    return total;
  }

  // ---------- TDP breakdown (reworked 2026-08-12) ----------

  computeTdpBreakdown() {
    // Radio-driven sections: watts = data-consumption x data-qty on the checked
    // input. Sections whose Liquid renders no data-consumption read 0 here;
    // 0W rows stay visible in the tooltip by design.
    const radioWatts = (name) => {
      const input = this.querySelector(`input[name="${name}"]:checked`);
      if (!input) return { watts: 0, detail: '' };
      const c = parseFloat(input.dataset.consumption) || 0;
      if (c === 0) return { watts: 0, detail: '' };
      const qty = Math.max(1, parseInt(input.dataset.qty) || 1);
      return { watts: c * qty, detail: qty > 1 ? `${qty}x ${c}W` : '' };
    };

    // Drives: DEDUPE BY QSKU. A recommended drive renders twice (Recommended
    // section plus its home group, duplicate-not-remove ruling); both DOM
    // inputs share one qsku and one selectedDrives entry, so counting each
    // input double-counted the watts. Count each qsku once.
    let driveWatts = 0;
    const seenQskus = new Set();
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      const qsku = input.dataset.driveQsku;
      if (!qsku || seenQskus.has(qsku)) return;
      seenQskus.add(qsku);
      const qty = this.selectedDrives.get(qsku)?.qty || 0;
      if (qty > 0) {
        const priceEl = input.closest('.configurator__card')?.querySelector('.q-drive-price');
        driveWatts += (parseFloat(priceEl?.dataset.consumption) || 0) * qty;
      }
    });

    const rows = [
      Object.assign({ label: 'Chassis Base' }, { watts: parseFloat(this.dataset.baseConsumption) || 0, detail: '' }),
      Object.assign({ label: 'Processor' }, radioWatts('processor-selection')),
      Object.assign({ label: 'Heat Sink' }, radioWatts('heatsink-selection')),
      Object.assign({ label: 'Memory (RAM)' }, radioWatts('memory-selection')),
      Object.assign({ label: 'RAID Controllers' }, radioWatts('raid-selection')),
      Object.assign({ label: 'Storage Drives' }, { watts: driveWatts, detail: '' }),
      Object.assign({ label: 'Remote Access' }, radioWatts('remote-selection')),
      Object.assign({ label: 'Power Supply' }, radioWatts('power-supply-selection')),
      Object.assign({ label: 'Network Cards' }, radioWatts('network-card-selection'))
    ];
    const total = rows.reduce((sum, r) => sum + r.watts, 0);
    return { rows, total };
  }

  setupTdpTooltip() {
    if (document.querySelector('.q-tdp-wrap')) return;
    const style = document.createElement('style');
    style.textContent = `
      .q-tdp-wrap { position: relative; display: inline-flex; align-items: center; gap: 0.35rem; }
      .q-tdp-trigger { width: 1.1rem; height: 1.1rem; border-radius: 50%; border: 1px solid currentColor; background: none; color: inherit; font-size: 0.7rem; font-weight: 600; line-height: 1; cursor: pointer; padding: 0; display: inline-flex; align-items: center; justify-content: center; }
      .q-tdp-tip { display: none; position: absolute; bottom: calc(100% + 0.5rem); right: 0; background: #fff; color: #222; border: 1px solid #ddd; border-radius: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); padding: 0.75rem 1rem; min-width: 260px; z-index: 100; font-size: 0.85rem; text-align: left; }
      .q-tdp-wrap.q-tdp-open .q-tdp-tip { display: block; }
      .q-tdp-row { display: flex; justify-content: space-between; gap: 1.5rem; padding: 0.15rem 0; white-space: nowrap; }
      .q-tdp-row--total { border-top: 1px solid #ddd; margin-top: 0.35rem; padding-top: 0.4rem; font-weight: 600; }
      .q-tdp-detail { opacity: 0.65; font-size: 0.9em; margin-left: 0.35rem; }
      .q-info-expander__summary { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; cursor: pointer; list-style: none; font-weight: 600; }
      .q-info-expander__summary::-webkit-details-marker { display: none; }
      .q-info-expander__summary::marker { content: ''; }
      .q-info-expander__icon { flex: none; width: 1.3rem; height: 1.3rem; border-radius: 50%; border: 1px solid currentColor; display: inline-flex; align-items: center; justify-content: center; font-size: 1rem; line-height: 1; }
      .q-info-expander__icon::before { content: '+'; }
      .q-info-expander[open] .q-info-expander__icon::before { content: '\\2212'; }
      .q-info-expander__body { margin-top: 0.75rem; }
      .q-config-controls { display: flex; flex-direction: column; align-items: flex-start; gap: 0.75rem; margin: 0 0 1.25rem; }
      .q-config-current { font-size: 1.05rem; font-weight: 600; }
      .q-config-current .product-summary-price { margin-left: 0.35rem; }
      .q-config-btns { display: flex; flex-wrap: wrap; gap: 0.6rem; }
      .q-config-btn { border: 1px solid currentColor; background: none; color: inherit; border-radius: 6px; padding: 0.5rem 1rem; font-weight: 600; cursor: pointer; }
      .q-config-btn:hover { background: rgba(0, 0, 0, 0.06); }
      .q-config-btn--active { background: #e0eeff; border-color: #083d81; color: #083d81; }
      .q-config-btn--active:hover { background: #e0eeff; }
      .configurator__card-title { margin: 0; }
      .configurator__card-content { padding: 0.5em 1em; }
      .q-price-delta { line-height: 1.2; }
      .configurator__drives-section .configurator__card-meta { display: none; }
    `;
    document.head.appendChild(style);
    document.querySelectorAll('.product-summary-watts').forEach((el) => {
      const wrap = document.createElement('span');
      wrap.className = 'q-tdp-wrap';
      el.parentNode.insertBefore(wrap, el);
      wrap.appendChild(el);
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'q-tdp-trigger';
      trigger.setAttribute('aria-label', 'Show estimated power draw breakdown');
      trigger.textContent = 'i';
      const tip = document.createElement('div');
      tip.className = 'q-tdp-tip';
      wrap.appendChild(trigger);
      wrap.appendChild(tip);
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        wrap.classList.toggle('q-tdp-open');
      });
      wrap.addEventListener('mouseenter', () => wrap.classList.add('q-tdp-open'));
      wrap.addEventListener('mouseleave', () => wrap.classList.remove('q-tdp-open'));
    });
    document.addEventListener('click', () => {
      document.querySelectorAll('.q-tdp-wrap.q-tdp-open').forEach((w) => w.classList.remove('q-tdp-open'));
    });
  }

  updateTdpTooltip(breakdown) {
    const rowsHtml = breakdown.rows.map((r) => {
      const detail = r.detail ? `<span class="q-tdp-detail">(${r.detail})</span>` : '';
      return `<div class="q-tdp-row"><span>${r.label}${detail}</span><span>${Math.round(r.watts)}W</span></div>`;
    }).join('');
    const totalHtml = `<div class="q-tdp-row q-tdp-row--total"><span>Estimated TDP Draw</span><span>${Math.round(breakdown.total)}W</span></div>`;
    document.querySelectorAll('.q-tdp-tip').forEach((tip) => {
      tip.innerHTML = rowsHtml + totalHtml;
    });
  }

  recalc() {
    const base = this.baseTotalCents();

    // Hybrid absolute + delta on every radio card group (ratified 4.2).
    // Scratch amendment (2026-08-20): when NOTHING is selected in a group,
    // every card shows its plain absolute price (the delta would equal it).
    const groups = ['processor-selection', 'heatsink-selection', 'memory-selection', 'raid-selection', 'remote-selection', 'power-supply-selection', 'network-card-selection'].concat(this.addonGroupNames());
    groups.forEach((name) => {
      const checked = this.querySelector(`input[name="${name}"]:checked`);
      const selectedLine = checked ? (parseInt(checked.dataset.unitCents) || 0) * (parseInt(checked.dataset.qty) || 0) : 0;
      this.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
        const priceEl = input.closest('.configurator__card')?.querySelector('.q-price');
        if (!priceEl || priceEl.dataset.included) return;
        const line = (parseInt(input.dataset.unitCents) || 0) * (parseInt(input.dataset.qty) || 0);
        if (input === checked || !checked) {
          priceEl.textContent = this.formatMoney(line);
        } else {
          const diff = line - selectedLine;
          priceEl.innerHTML = `${this.formatMoney(line)}<span class="q-price-delta" style="display:block;font-size:0.85em;opacity:0.75;">[${diff >= 0 ? '+' : ''}${this.formatMoney(diff)}]</span>`;
        }
      });
    });

    // Drive prices (tray display amendment 2026-08-27): tray-inclusive
    // per unit at qty 0, tray-inclusive line total once added. The ff
    // comes from the same card's stepper input (data-drive-ff);
    // qTrayAdderCents returns 0 with no trayMap, reverting to drive-only.
    this.querySelectorAll('.q-drive-price').forEach((priceEl) => {
      const card = priceEl.closest('.configurator__card');
      const input = card.querySelector('.quantity__input');
      const qty = parseInt(input.value) || 0;
      const unit = (parseInt(priceEl.dataset.unitCents) || 0) + this.qTrayAdderCents(input.dataset.driveFf);
      priceEl.textContent = `+${this.formatMoney(qty > 0 ? unit * qty : unit)}`;
    });
    this.renderEmptyTray();

    // OS option labels: absolute + delta
    const osSel = this.selectedOs();
    const osSelCents = osSel ? osSel.unitCents : 0;
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((select) => {
      Array.from(select.options).forEach((opt) => {
        if (opt.value === '') return;
        const cents = parseInt(opt.dataset.unitCents) || 0;
        const baseText = opt.textContent.split(' [')[0].split(' (')[0].trim();
        const diff = cents - osSelCents;
        if (osSel && opt.dataset.qsku === osSel.qsku && select.value === opt.value) {
          opt.textContent = cents > 0 ? `${baseText} (${this.formatMoney(cents)})` : baseText;
        } else if (cents === 0 && diff === 0) {
          opt.textContent = baseText;
        } else {
          opt.textContent = `${baseText} (${this.formatMoney(cents)}, ${diff >= 0 ? '+' : ''}${this.formatMoney(diff)})`;
        }
      });
    });

    // Warranty price from settings markups
    const warrantyPriceEl = this.querySelector('.q-warranty-price');
    const yearsSelect = this.querySelector('#q-warranty-years');
    if (warrantyPriceEl && yearsSelect) {
      const markup = this.tierMarkup(yearsSelect.value);
      if (markup === null) {
        warrantyPriceEl.textContent = this.settingsError ? 'unavailable' : '...';
      } else {
        warrantyPriceEl.textContent = `+${this.formatMoney(Math.round(base * markup / 100))}`;
      }
    }

    // Total with warranty. Every .product-summary-price element on the page
    // (summary bar plus the Currently Configured control bar span) receives
    // the same figure: one price source.
    const label = this.selectedWarrantyLabel();
    const markup = this.tierMarkup(label);
    const total = markup === null ? base : Math.round(base * (1 + markup / 100));
    document.querySelectorAll('.product-summary-price').forEach((el) => {
      el.textContent = this.formatMoney(total);
    });

    // TDP: per-section breakdown, drive twin dedupe, tooltip rows incl. 0W
    const breakdown = this.computeTdpBreakdown();
    document.querySelectorAll('.product-summary-watts').forEach((el) => {
      el.textContent = `${Math.round(breakdown.total)}W`;
    });
    this.updateTdpTooltip(breakdown);
    this.renderPsuWatt(breakdown.total);

    // 4B memory UX: seg state, select tails, and the Populates line
    // re-render on every pass, from the same selection readers.
    this.renderMemoryUx();

    // 4C drives UX: pinned selection box, twin sync, and bayline re-render
    // on every pass, from selectedDrives (the single source).
    this.renderDrivesUx();

    // 4D warranty tier buttons: amounts track the live base; the heatsink
    // notice's AUTO-SELECTED flag tracks the standard selection.
    this.renderWarrantyUx(base);
    const hsFlag = this.querySelector('[data-q-hs-flag]');
    if (hsFlag) {
      const hsSel = this.querySelector('input[name="heatsink-selection"]:checked');
      hsFlag.style.display = hsSel && hsSel.value === 'standard-heatsink' ? '' : 'none';
    }

    // Redesign summary pane (mock v11): rendered LAST so it summarizes the
    // same recalc pass, from the same readers; never a parallel state.
    this.renderSummaryPane({ base, total, label, markup, tdpDraw: breakdown.total, psuRating: this.psuRatingWatts() });
  }

  // ---------- summary pane (wizard redesign, mock v11 port) ----------

  setupSummaryPane() {
    this.qPaneReady = true;
    const paneLines = document.getElementById('q-led-lines');
    const thumb = paneLines ? paneLines.querySelector('.q-led-thumb') : null;
    this.qBaseThumbHtml = thumb ? thumb.outerHTML : '';
    paneLines?.setAttribute('data-q-lines', '');
    document.getElementById('q-t-warr-label')?.setAttribute('data-q-warr-label', '');
    document.getElementById('q-t-warr')?.setAttribute('data-q-warr', '');

    // Pane CTAs: reuse the shared cart/quote flows with per-button spinners.
    const pane = document.getElementById('q-pane');
    if (pane && !pane.querySelector('.q-pane-cta')) {
      const quoteBtn = document.createElement('button');
      quoteBtn.type = 'button';
      quoteBtn.className = 'q-pane-cta';
      quoteBtn.textContent = 'Add To Quote';
      quoteBtn.addEventListener('click', () => this.requestQuote(quoteBtn));
      const hint = document.createElement('div');
      hint.className = 'q-pane-ctahint';
      hint.setAttribute('data-q-ctahint', '');
      hint.style.display = 'none';
      const cartBtn = document.createElement('button');
      cartBtn.type = 'button';
      cartBtn.className = 'q-pane-cta2';
      cartBtn.textContent = 'Add To Cart';
      cartBtn.addEventListener('click', () => this.addToCart(cartBtn));
      pane.appendChild(quoteBtn);
      pane.appendChild(hint);
      pane.appendChild(cartBtn);
    }

    // Mobile expander: mirrored ledger inside the fixed bar; tapping the bar
    // (not its buttons or controls) toggles it open in place.
    const exp = document.getElementById('q-bar-expander');
    if (exp && !exp.querySelector('.ledger')) {
      exp.innerHTML = '<div class="ledger"><div class="led-lines" data-q-lines></div><div class="led-tot"><div class="trow"><span data-q-warr-label>Warranty</span><span class="tv" data-q-warr>...</span></div><div class="trow grand"><span>Build Total</span><span class="tv product-summary-price"></span></div><div class="est">Live estimate. Final price is confirmed when your quote is created.</div></div></div>';
      const bar = document.querySelector('.product-summary-bar');
      if (bar) {
        bar.addEventListener('click', (event) => {
          if (event.target.closest('button, select, a, input, .q-tdp-wrap, .q-bar-expander')) return;
          bar.classList.toggle('q-bar-open');
        });
      }
    }

    // Clickable error notices and summary line navigation (pane and
    // expander copies), delegated once.
    if (!this.qNoticeDelegated) {
      this.qNoticeDelegated = true;
      document.addEventListener('click', (event) => {
        const note = event.target.closest('.led-note.err[data-q-goto]');
        if (note) {
          this.qGotoSection(note.getAttribute('data-q-goto'));
          return;
        }
        const line = event.target.closest('[data-q-sec]');
        if (line && line.closest('[data-q-lines]')) {
          this.qGotoSlot(line.getAttribute('data-q-sec'));
        }
      });
    }

    // Desktop: pane owns the CTAs from here on; the redesign CSS hides the
    // fixed bar at desktop widths ONLY under this class, so a JS failure can
    // never strand the page without working buttons.
    document.querySelector('.product-page')?.classList.add('q-pane-live');
  }

  qGotoSection(name) {
    const map = {
      'Processor': '#configurator-processors',
      'Memory (RAM)': '#configurator-memory',
      'Remote Access': '#configurator-remotes',
      'Power Supply': '#configurator-power-supplies',
      'Network Card': '#configurator-network-cards',
      'Operating System': '.configurator__dropdown[name="os-selection"]'
    };
    this.qGotoTarget(map[name]);
  }

  qGotoSlot(key) {
    // Summary line click navigation (4D punch 2): slot key to a section
    // anchor. Grid IDs where the snippet has them; input/class lookups
    // where it does not, resolved to the enclosing details the same way.
    const map = {
      'base': '.q-config-controls',
      's1': '#configurator-processors',
      's2': 'input[name="heatsink-selection"]',
      's3': '#configurator-memory',
      's4': 'input[name="raid-selection"]',
      's5': '.configurator__drives-section',
      's6': '#configurator-remotes',
      's7': '#configurator-power-supplies',
      's8': '#configurator-network-cards',
      's9': '.configurator__dropdown[name="os-selection"]',
      's10': '.q-addon-grid'
    };
    this.qGotoTarget(map[key]);
  }

  qGotoTarget(sel) {
    if (!sel) return;
    const target = this.querySelector(sel);
    if (!target) return;
    const details = target.closest('details');
    if (details) {
      details.open = true;
      const content = details.querySelector('.configurator__content-wrapper');
      if (content) {
        content.style.height = 'auto';
        content.style.opacity = '1';
      }
    }
    document.querySelector('.product-summary-bar')?.classList.remove('q-bar-open');
    (details || target).scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  flagValidationFailure(context) {
    const missing = this.missingSelections();
    if (document.getElementById('q-led-lines')) {
      // Redesign path (red-after-attempt ruling): clickable notices in the
      // pane replace the legacy alert; open the mobile expander so they are
      // visible when the bar is the active summary surface.
      this.validationAttempted = true;
      this.recalc();
      const bar = document.querySelector('.product-summary-bar');
      if (bar && bar.offsetParent !== null) bar.classList.add('q-bar-open');
    } else {
      alert(`Please complete the required selections before ${context}:\n- ${missing.join('\n- ')}`);
    }
  }

  renderSummaryPane(ctx) {
    const paneLines = document.getElementById('q-led-lines');
    if (!paneLines) return; // redesign shell absent: legacy layout, no-op
    if (!this.qPaneReady) this.setupSummaryPane();
    const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const money = (c) => this.formatMoney(c);
    const lineHtml = (l) => {
      const num = l.noNum ? '' : `<span class="lnum${l.base ? ' base' : ''}">${l.n}</span>`;
      const sku = l.sku ? `<span class="lsku">${esc(l.sku)}</span>` : '';
      const qty = l.qty > 0 && !l.included ? `<span class="lqty">${l.qty} &times; ${money(l.unit)}</span>` : '';
      const amt = l.included ? 'Included' : money(l.amt);
      const sec = l.sec ? ` data-q-sec="${l.sec}"` : '';
      return `<div class="led-line${l.auto ? ' auto' : ''}"${sec}>${num}<span class="lname">${esc(l.name)}${sku}</span>${qty}<span class="lamt">${amt}</span></div>`;
    };
    // Every numbered slot renders on every pass; empty sections get a muted
    // placeholder so the numbering never skips (owner request 2026-08-26).
    const emptyLine = (n, name, sec) => `<div class="led-line q-led-none" data-q-sec="${sec}"><span class="lnum">${n}</span><span class="lname">${name}</span><span class="lamt">None selected</span></div>`;

    let html = '';
    const missing = this.validationAttempted ? this.missingSelections() : [];
    if (missing.length) {
      html += missing.map((m) => `<div class="led-note err" data-q-goto="${esc(m)}">&#9888; ${esc(m)}: selection required</div>`).join('');
    }
    let lineCount = 1;
    html += `<div class="led-line" data-q-sec="base">${this.qBaseThumbHtml}<span class="lnum base">B</span><span class="lname">${esc(this.dataset.productTitle || 'Base System')}<span class="lsku">${esc(this.dataset.chassisQsku || '')}</span></span><span class="lamt">${money(parseInt(this.dataset.chassisCents) || 0)}</span></div>`;

    const proc = this.readRadio('processor-selection');
    if (proc) { lineCount += 1; html += lineHtml({ n: 1, sec: 's1', name: proc.title, sku: proc.qsku, qty: proc.qty, unit: proc.unitCents, amt: proc.unitCents * proc.qty }); }
    else html += emptyLine(1, 'Processor', 's1');
    const hs = this.readRadio('heatsink-selection');
    if (hs) { lineCount += 1; html += lineHtml({ n: 2, sec: 's2', name: hs.title, sku: hs.qsku, qty: hs.qty, unit: hs.unitCents, amt: hs.unitCents * hs.qty }); }
    else if (this.querySelector('input[name="heatsink-selection"]:checked')) { lineCount += 1; html += lineHtml({ n: 2, sec: 's2', name: 'Standard Heatsink/Fan', included: true }); }
    else html += emptyLine(2, 'Heat Sink', 's2');
    const mem = this.selectedMemory();
    if (mem) { lineCount += 1; html += lineHtml({ n: 3, sec: 's3', name: mem.title, sku: mem.qsku, qty: mem.qty, unit: mem.unitCents, amt: mem.unitCents * mem.qty }); }
    else html += emptyLine(3, 'Memory (RAM)', 's3');
    const raid = this.readRadio('raid-selection');
    if (raid) { lineCount += 1; html += lineHtml({ n: 4, sec: 's4', name: raid.title, sku: raid.qsku, qty: raid.qty, unit: raid.unitCents, amt: raid.unitCents * raid.qty }); }
    else html += emptyLine(4, 'RAID Controllers', 's4');

    const driveMembers = [];
    this.selectedDrives.forEach((d, qsku) => {
      driveMembers.push({ noNum: true, name: (d.qty > 1 ? d.qty + 'x ' : '') + (d.title || qsku), sku: qsku, qty: d.qty, unit: d.unitCents, amt: d.unitCents * d.qty });
    });
    if (this.trayMap) {
      // Tray lines mirror baseTotalCents exactly (same trayMap source),
      // aggregated per tray qsku and flagged auto.
      const trayAgg = new Map();
      this.selectedDrives.forEach((d) => {
        (this.trayMap[d.ff] || []).forEach((t) => {
          const add = (t.qty_per_drive || 0) * d.qty;
          if (add <= 0) return;
          const cur = trayAgg.get(t.tray_qsku) || { qty: 0, unit: parseInt(t.unit_cents) || 0 };
          cur.qty += add;
          trayAgg.set(t.tray_qsku, cur);
        });
      });
      trayAgg.forEach((t, qsku) => {
        driveMembers.push({ noNum: true, auto: true, name: (t.qty > 1 ? t.qty + 'x ' : '') + 'Drive Tray', sku: qsku, qty: t.qty, unit: t.unit, amt: t.unit * t.qty });
      });
    }
    {
      // Empty drive trays (2026-10-22): one member in the Drives group.
      const et = this.emptyTrayInfo();
      if (et && et.qty > 0) driveMembers.push({ noNum: true, name: `${et.qty}x Empty Drive Trays (ship loose)`, sku: et.qsku, qty: et.qty, unit: et.unitCents, amt: et.unitCents * et.qty });
    }
    lineCount += driveMembers.length;
    if (driveMembers.length === 1) {
      html += lineHtml(Object.assign({}, driveMembers[0], { noNum: false, n: 5, sec: 's5' }));
    } else if (driveMembers.length > 1) {
      const gTotal = driveMembers.reduce((a, m) => a + m.amt, 0);
      html += `<div class="led-grp" data-q-sec="s5"><div class="g-head"><span class="lnum">5</span><span class="g-title">Drives</span><span class="g-amt">${money(gTotal)}</span></div><div class="g-box">${driveMembers.map((m) => lineHtml(m)).join('')}</div></div>`;
    } else {
      html += emptyLine(5, 'Storage Drives', 's5');
    }

    const remote = this.readRadio('remote-selection');
    if (remote) { lineCount += 1; html += lineHtml({ n: 6, sec: 's6', name: remote.title, sku: remote.qsku, qty: remote.qty, unit: remote.unitCents, amt: remote.unitCents * remote.qty }); }
    else html += emptyLine(6, 'Remote Access', 's6');
    const psu = this.readRadio('power-supply-selection');
    if (psu) { lineCount += 1; html += lineHtml({ n: 7, sec: 's7', name: psu.title, sku: psu.qsku, qty: psu.qty, unit: psu.unitCents, amt: psu.unitCents * psu.qty }); }
    else html += emptyLine(7, 'Power Supply', 's7');
    // 4D punch 3: over-rating alert directly under the PSU line whenever
    // the estimated draw exceeds the parsed single PSU rating. Display
    // only, never blocking; click navigates to the PSU section through the
    // existing led-note delegate.
    if (ctx.psuRating > 0 && ctx.tdpDraw > ctx.psuRating) {
      html += `<div class="led-note err" data-q-goto="Power Supply">&#9888; Estimated TDP draw ${Math.round(ctx.tdpDraw)}W exceeds the ${ctx.psuRating}W PSU rating</div>`;
    }
    const nic = this.readRadio('network-card-selection');
    if (nic) { lineCount += 1; html += lineHtml({ n: 8, sec: 's8', name: nic.title, sku: nic.qsku, qty: nic.qty, unit: nic.unitCents, amt: nic.unitCents * nic.qty }); }
    else if (this.querySelector('input[name="network-card-selection"]:checked')) { lineCount += 1; html += lineHtml({ n: 8, sec: 's8', name: 'No Add-On Network Card', included: true }); }
    else html += emptyLine(8, 'Network Cards', 's8');
    const os = this.selectedOs();
    if (os) { lineCount += 1; html += lineHtml({ n: 9, sec: 's9', name: os.title.split(' (')[0], sku: os.qsku, qty: 1, unit: os.unitCents, amt: os.unitCents }); }
    else html += emptyLine(9, 'Operating System', 's9');

    // Slot 10 Add-Ons: one pick renders as a numbered line, several as a
    // grouped box (the drives pattern), none as the muted placeholder.
    const addons = this.selectedAddons();
    lineCount += addons.length;
    if (addons.length === 1) {
      const a = addons[0];
      html += lineHtml({ n: 10, sec: 's10', name: a.title, sku: a.qsku, qty: a.qty, unit: a.unitCents, amt: a.unitCents * a.qty });
    } else if (addons.length > 1) {
      const aTotal = addons.reduce((sum, a) => sum + a.unitCents * a.qty, 0);
      const aMembers = addons.map((a) => lineHtml({ noNum: true, name: a.title, sku: a.qsku, qty: a.qty, unit: a.unitCents, amt: a.unitCents * a.qty })).join('');
      html += `<div class="led-grp" data-q-sec="s10"><div class="g-head"><span class="lnum">10</span><span class="g-title">Add-Ons</span><span class="g-amt">${money(aTotal)}</span></div><div class="g-box">${aMembers}</div></div>`;
    } else {
      html += emptyLine(10, 'Add-Ons', 's10');
    }

    document.querySelectorAll('[data-q-lines]').forEach((el) => { el.innerHTML = html; });

    // Warranty rows: label mirrors the selected tier; amount is included on
    // the base tier and a dollar markup otherwise, with honest placeholders
    // on missing settings (fail loud, never a fabricated number).
    const shortLabel = String(ctx.label || '').replace(' Warranty', '');
    let warrValue;
    if (ctx.markup === null) warrValue = this.settingsError ? 'unavailable' : '...';
    else if (ctx.markup === 0) warrValue = 'included';
    else warrValue = '+' + money(Math.round(ctx.base * ctx.markup / 100));
    document.querySelectorAll('[data-q-warr-label]').forEach((el) => { el.textContent = 'Warranty (' + shortLabel + ')'; });
    document.querySelectorAll('[data-q-warr]').forEach((el) => { el.textContent = warrValue; });
    document.querySelectorAll('.q-bar-expander .product-summary-price').forEach((el) => { el.textContent = money(ctx.total); });

    // CTA hint under the pane buttons
    document.querySelectorAll('[data-q-ctahint]').forEach((el) => {
      if (missing.length) {
        el.style.display = 'block';
        el.textContent = 'Resolve ' + missing.length + (missing.length === 1 ? ' item' : ' items') + ' to continue';
      } else {
        el.style.display = 'none';
        el.textContent = '';
      }
    });

    // 4D mobile bar meta: the chevron pill and bar label reflect this same
    // pass ("Review", or the err state with "N items needed" and a
    // "Partial total" label after a failed attempt).
    const pill = document.querySelector('[data-q-bar-pill]');
    if (pill) {
      if (missing.length) {
        pill.textContent = missing.length + (missing.length === 1 ? ' item needed' : ' items needed');
        pill.classList.add('err');
      } else {
        // Owner request 2026-08-26: a plain "Review" invite instead of the
        // vague line count; the err state keeps its "N items needed" copy.
        pill.textContent = 'Review';
        pill.classList.remove('err');
      }
    }
    const barLabel = document.querySelector('[data-q-bar-label]');
    if (barLabel) barLabel.textContent = missing.length ? 'Partial total' : 'Build total';
  }

  // ---------- validation ----------

  validateSelections() {
    let valid = true;
    const requirements = [
      { grid: '#configurator-processors', ok: !!this.readRadio('processor-selection') },
      { grid: '#configurator-memory', ok: !!this.selectedMemory() },
      { grid: '#configurator-remotes', ok: !!this.readRadio('remote-selection') },
      { grid: '#configurator-power-supplies', ok: !!this.readRadio('power-supply-selection') },
      { grid: '#configurator-network-cards', ok: !!this.querySelector('input[name="network-card-selection"]:checked') }
    ];
    requirements.forEach((r) => {
      const grid = this.querySelector(r.grid);
      if (!grid) return;
      const details = grid.closest('details');
      const err = details?.querySelector('.configurator__error-message');
      if (!r.ok) {
        valid = false;
        if (details) details.style.border = '2px solid red';
        if (err) err.style.display = 'block';
      } else {
        if (details) details.style.border = '';
        if (err) err.style.display = 'none';
      }
    });
    const osOk = !!this.selectedOs();
    const osSection = this.querySelector('.configurator__dropdown[name="os-selection"]')?.closest('details');
    const osErr = osSection?.querySelector('.configurator__error-message');
    if (!osOk) {
      valid = false;
      if (osSection) osSection.style.border = '2px solid red';
      if (osErr) osErr.style.display = 'block';
    } else {
      if (osSection) osSection.style.border = '';
      if (osErr) osErr.style.display = 'none';
    }
    return valid;
  }

  // ---------- backend ----------

  async sendConfiguration() {
    const payload = {
      action: 'create',
      title: this.dataset.templateName,
      template_id: this.dataset.templateId,
      warranty_tier: this.selectedWarrantyLabel(),
      lines: this.buildLines(),
      // Empty drive trays (2026-10-22): absent when zero, so builds without
      // empties send the exact pre-v34 payload.
      empty_trays: this.emptyTrayQty > 0 ? { qty: this.emptyTrayQty } : undefined
    };
    const response = await fetch(this.dataset.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.dataset.anonKey}`
      },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok) {
      const detail = Array.isArray(data.errors) && data.errors.length ? data.errors.join('; ') : (data.error || `HTTP ${response.status}`);
      throw new Error(detail);
    }
    return data;
  }

  getConfigurationProperties() {
    const properties = {};
    const names = [];
    const proc = this.readRadio('processor-selection');
    if (proc) names.push(`Processor: ${proc.title}`);
    const heatsink = this.readRadio('heatsink-selection');
    if (heatsink) names.push(`Heatsink: ${heatsink.title}`);
    const memory = this.selectedMemory();
    if (memory) names.push(`Memory: ${memory.title}`);
    const raid = this.readRadio('raid-selection');
    if (raid) names.push(`RAID Controller: ${raid.title}`);
    this.selectedDrives.forEach((d) => {
      names.push(d.qty > 1 ? `Storage Drive: ${d.qty}x ${d.title}` : `Storage Drive: ${d.title}`);
    });
    if (this.trayMap) {
      // Resolved tray lines (same aggregation as the pane and
      // baseTotalCents); the backend composes and prices these, this only
      // makes them visible in the draft order property list.
      const trayAgg = new Map();
      this.selectedDrives.forEach((d) => {
        (this.trayMap[d.ff] || []).forEach((t) => {
          const add = (t.qty_per_drive || 0) * d.qty;
          if (add <= 0) return;
          trayAgg.set(t.tray_qsku, (trayAgg.get(t.tray_qsku) || 0) + add);
        });
      });
      trayAgg.forEach((qty, qsku) => {
        names.push(qty > 1 ? `Drive Tray: ${qty}x ${qsku}` : `Drive Tray: ${qsku}`);
      });
    }
    {
      const et = this.emptyTrayInfo();
      if (et && et.qty > 0) names.push(`Empty Drive Trays: ${et.qty}x ${et.qsku} (ship loose)`);
    }
    const remote = this.readRadio('remote-selection');
    if (remote) names.push(`Remote Access: ${remote.title}`);
    const psu = this.readRadio('power-supply-selection');
    if (psu) names.push(`Power Supply: ${psu.title}`);
    const nic = this.readRadio('network-card-selection');
    if (nic) names.push(`Network Card: ${nic.title}`);
    const os = this.selectedOs();
    if (os) names.push(`Operating System: ${os.title}`);
    this.selectedAddons().forEach((a) => names.push(`Add-On: ${a.title}`));
    names.push(`Warranty: ${this.selectedWarrantyLabel()}`);
    if (names.length) properties['_bundle_components'] = names.join('||');
    return properties;
  }

  createLoadingButton(button, alternateColor = false) {
    const existingText = button.textContent.trim();
    button.textContent = '';
    const buttonText = document.createElement('span');
    buttonText.className = 'button-text';
    buttonText.textContent = existingText;
    const spinner = document.createElement('span');
    spinner.className = `loading-spinner hidden ${alternateColor ? 'loading-spinner--alternate' : ''}`;
    spinner.innerHTML = `
      <svg aria-hidden="true" class="icon icon-spinner" viewBox="0 0 20 20">
        <path d="M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z"/>
      </svg>`;
    button.appendChild(buttonText);
    button.appendChild(spinner);
    return {
      startLoading: () => {
        button.setAttribute('aria-busy', 'true');
        button.classList.add('button--loading');
        button.disabled = true;
        spinner.classList.remove('hidden');
      },
      stopLoading: () => {
        button.removeAttribute('aria-busy');
        button.disabled = false;
        button.classList.remove('button--loading');
        spinner.classList.add('hidden');
      }
    };
  }

  setupActions() {
    const cartBtn = document.querySelector('[data-add-to-cart-configurator]');
    cartBtn?.addEventListener('click', () => this.addToCart(cartBtn));
    const quoteBtn = document.querySelector('[data-quote-configurator]');
    quoteBtn?.addEventListener('click', () => this.requestQuote(quoteBtn));
  }

  async addToCart(button) {
    button = button || document.querySelector('[data-add-to-cart-configurator]');
    if (!button) return;
    if (!this.validateSelections()) {
      this.flagValidationFailure('adding to cart');
      return;
    }
    const loadingButton = this.createLoadingButton(button);
    try {
      loadingButton.startLoading();
      const configResponse = await this.sendConfiguration();
      const variantId = configResponse.bundleVariantId || configResponse.variantId;
      if (!variantId) throw new Error('Invalid configuration response');
      if (configResponse.isDuplicate) console.log('Using existing bundle:', configResponse.bundleCode);
      const matches = String(variantId).match(/ProductVariant\/(\d+)/);
      const variantNumericId = matches ? matches[1] : null;
      if (!variantNumericId) throw new Error('Invalid variant ID format');
      const formData = {
        items: [{
          id: variantNumericId,
          quantity: 1,
          properties: {
            _total_price: configResponse.total,
            ...this.getConfigurationProperties()
          }
        }]
      };
      const maxRetries = 5;
      const retryDelay = 5000;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const response = await fetch(`${window.location.origin}/cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(formData)
        });
        const responseData = await response.json();
        if (response.ok) {
          window.location.href = window.location.origin + '/cart';
          return;
        }
        if (response.status === 422 && attempt < maxRetries) {
          console.warn(`Cart add attempt ${attempt} failed (422), retrying in ${retryDelay / 1000}s...`, responseData);
          await new Promise((r) => setTimeout(r, retryDelay));
          continue;
        }
        throw new Error(responseData.description || 'Error adding to cart (note: test-phase bundles are DRAFT and cannot be carted until cutover)');
      }
    } catch (error) {
      console.error('Detailed Error:', error);
      alert(`Failed to add item to cart: ${error.message}`);
    } finally {
      loadingButton.stopLoading();
    }
  }

  async requestQuote(button) {
    button = button || document.querySelector('[data-quote-configurator]');
    if (!button) return;
    if (!this.validateSelections()) {
      this.flagValidationFailure('requesting a quote');
      return;
    }
    let loadingButton = null;
    try {
      loadingButton = this.createLoadingButton(button, true);
      loadingButton.startLoading();

      const configResponse = await this.sendConfiguration();
      const variantId = configResponse.bundleVariantId || configResponse.variantId;
      if (!variantId) throw new Error('Unable to create configuration. Please try again or contact support.');
      if (configResponse.isDuplicate) console.log('Using existing bundle:', configResponse.bundleCode);

      const { total } = configResponse;
      const properties = this.getConfigurationProperties();
      const productTitle = `${this.dataset.productTitle}-configured` || 'Server Configuration';
      let productImage = this.dataset.productImageUrl || '';
      if (productImage && productImage.startsWith('files/')) {
        productImage = productImage.replace('files/', 'https://savemyserver.com/cdn/shop/files/');
      }

      const cartItem = {
        variantId: variantId,
        quantity: 1,
        title: productTitle,
        price: total,
        image: productImage,
        properties: properties,
        configuration: {
          title: this.dataset.templateName,
          bundleCode: configResponse.bundleCode,
          lines: this.buildLines(),
          warranty_tier: this.selectedWarrantyLabel(),
          empty_trays: this.emptyTrayQty > 0 ? { qty: this.emptyTrayQty } : undefined
        }
      };

      if (loadingButton) {
        loadingButton.stopLoading();
        loadingButton = null;
      }

      const thresholdDollars = parseFloat(this.dataset.quoteThreshold) || 500;
      const thresholdCents = thresholdDollars * 100;

      if (total < thresholdCents) {
        this.saveToDraftCartFallback(cartItem);
        document.dispatchEvent(new CustomEvent('quote-cart-updated', {
          detail: { action: 'add', item: cartItem }
        }));
        if (window.QuoteSuccessDialog && window.SMS_QUOTE_SETTINGS) {
          window.QuoteSuccessDialog.show({
            title: window.SMS_QUOTE_SETTINGS.addedTitle,
            message: window.SMS_QUOTE_SETTINGS.addedMessage,
            buttonText: 'View Quote Cart',
            buttonUrl: '/pages/quote-cart'
          });
        }
      } else {
        if (window.SMS_QuoteModal && window.SMS_QuoteModal.showForSingleItem) {
          window.SMS_QuoteModal.showForSingleItem(cartItem);
        } else {
          throw new Error('Quote modal system not loaded. Please refresh the page and try again.');
        }
      }
    } catch (error) {
      console.error('Quote Error:', error);
      let userMessage = 'Failed to create quote: ';
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        userMessage += 'Network connection error. Please check your internet connection and try again.';
      } else if (error.message.includes('timeout')) {
        userMessage += 'Request timed out. Please try again.';
      } else {
        userMessage += error.message;
      }
      alert(userMessage);
    } finally {
      if (loadingButton) loadingButton.stopLoading();
    }
  }

  saveToDraftCartFallback(item) {
    const storageKey = 'sms_quote_cart';
    let cart;
    try {
      const cartData = localStorage.getItem(storageKey);
      cart = cartData ? JSON.parse(cartData) : { items: [], createdAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error reading quote cart:', error);
      cart = { items: [], createdAt: new Date().toISOString() };
    }
    const existingItem = cart.items.find((cartItem) => {
      if (cartItem.variantId !== item.variantId) return false;
      const props1 = cartItem.properties || {};
      const props2 = item.properties || {};
      const keys1 = Object.keys(props1).sort();
      const keys2 = Object.keys(props2).sort();
      if (keys1.length !== keys2.length) return false;
      for (const key of keys1) {
        if (props1[key] !== props2[key]) return false;
      }
      return true;
    });
    if (existingItem) {
      existingItem.quantity = (existingItem.quantity || 1) + (item.quantity || 1);
    } else {
      const itemId = `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      cart.items.push({ id: itemId, ...item, addedAt: new Date().toISOString() });
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving to quote cart:', error);
      throw new Error('Unable to save to quote cart');
    }
  }

  // ---------- formatting ----------

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return '$0.00';
    }
  }

  formatGB(gb) {
    if (gb >= 1024 && gb % 1024 === 0) return `${gb / 1024}TB`;
    return `${gb}GB`;
  }
}

customElements.define('server-configurator-quantify', ServerConfiguratorQuantify);
